import {
  Department,
  DepartmentPayload,
  DepartmentUpdatePayload,
  LoginResponse,
  PortalModule,
  RoleTemplate,
  SessionResponse,
  ThemeConfig,
  User,
  UserPayload,
  WbReview,
  WbReviewCabinet,
  WbReviewCabinetPayload,
  WbReviewListResponse,
  WbReviewSettings,
  WbReviewSummary,
  WbReviewBulkPublishJobStatus,
  WbReviewFilters,
  LabelMakerBootstrap,
  LabelMakerBulkDeleteResponse,
  LabelMakerCareIcon,
  LabelMakerCodesExtractResult,
  LabelMakerDmRoundtripResponse,
  LabelMakerFieldAliasesResponse,
  LabelMakerImportResult,
  LabelMakerManagedCareIcon,
  LabelMakerProduct,
  LabelMakerProductPayload,
  LabelMakerTemplate,
  LabelMakerTemplatePayload,
  LabelMakerTemplateTransferResult,
  LabelMakerRenderJobStatus,
  LabelMakerRenderJobStartResponse,
  LabelMakerWbCabinet,
  LabelMakerWbCabinetPayload,
  LabelMakerWbImportResult,
  RoadmapBoard,
  RoadmapBoardPayload,
  RoadmapSuggestion,
  TgBot,
  TgFlow,
  TgFlowDetail,
  TgApplication,
  TgCareTicket,
  TgNodeGroup,
  TgRaffleEntry,
  TgPromoPool,
  TgPromoCode,
  TgBroadcast,
  TgUser,
  WbAnalyticsCabinet,
  WbAnalyticsCabinetPayload,
  WbAnalyticsArticle,
  WbAnalyticsCostPrice,
  SalesReport,
  WbAnalyticsWeekRow,
  WbAnalyticsStockItem,
  WbAnalyticsArticleDetailed,
  WbAnalyticsArticleWeekRow,
  WbExtraCost,
  WbSeasonality,
  WbFunnelRow,
  WbPaidStorageSummaryRow,
  WbSkuPlRow,
  MppulseDashboard,
  AdvAnalytics,
  RnpPulse,
  PnlOpiy,
  PnlOpiyMonth,
  PnlOpiyRange,
  PnlOpiyDaySkus,
} from "@/lib/types";
import { TOKEN_STORAGE_KEY } from "@/lib/theme";
import { beginTopProgress } from "@/lib/top-progress";

function resolveApiBase() {
  const explicitBase = process.env.NEXT_PUBLIC_API_BASE?.trim();
  if (explicitBase) return explicitBase.replace(/\/+$/, "");

  if (typeof window === "undefined") {
    return "http://localhost:8000/api";
  }

  const { protocol, hostname } = window.location;

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return `${protocol}//${hostname}:8000/api`;
  }

  if (hostname.startsWith("portal.")) {
    return `${protocol}//api.${hostname.slice("portal.".length)}/api`;
  }

  if (hostname.startsWith("dev.")) {
    return `${protocol}//dev-api.${hostname.slice("dev.".length)}/api`;
  }

  // yuc.konmak.ru (фронт) → yuc.api.konmak.ru (бэк) — dev в интернете.
  if (hostname.startsWith("yuc.")) {
    return `${protocol}//yuc.api.${hostname.slice("yuc.".length)}/api`;
  }

  return `${protocol}//${hostname}:8000/api`;
}

export const API_BASE = resolveApiBase();

type RequestOptions = RequestInit & {
  auth?: boolean;
};

function normalizeQueryValue(value: unknown) {
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) return typeof value[0] === "string" ? value[0].trim() : "";
  return "";
}

// ── Лёгкий кэш GET + дедуп «в полёте» ───────────────────────────────────────
// Снимает повторные запросы при переключении вкладок (возврат на вкладку в
// пределах TTL отдаёт кэш) и коалесцирует одновременные одинаковые GET в один
// сетевой вызов. Любая мутация (POST/PUT/PATCH/DELETE) через apiFetch полностью
// сбрасывает кэш — поэтому свежесть после правок не страдает (в т.ч. дебаунс-
// рефетч юнит-экономики из F3).
//
// ВОЛАТИЛЬНЫЕ эндпоинты НЕ кэшируем: статусы синков, summary, поллинг — иначе
// прогресс-бары «застынут» на устаревшем значении. Переисключить безопасно
// (просто не оптимизируем), недоисключить — нет (сломаем поллинг), поэтому
// список намеренно широкий.
const GET_CACHE_TTL_MS = 8_000;
// ⚠️ `reveal` и `totp` — сейф «Пароли и доступы». Кэшировать их нельзя по двум
// причинам: (1) повторный показ пароля не дошёл бы до бэкенда и не попал бы в
// журнал доступа, а журнал — единственная компенсация за серверное шифрование;
// (2) код двухфакторки живёт 30 секунд, из кэша он приезжал бы уже протухшим.
// Заодно открытое значение не оседает в Map на стороне вкладки.
const NO_CACHE_RE = /(status|summary|sync|progress|refresh|bulk-job|session|notification|\/team\/|imagegen|workflows|\/runs|reveal|totp)/i;
type GetCacheEntry = { ts: number; data: unknown };
const getCache = new Map<string, GetCacheEntry>();
const inflightGet = new Map<string, Promise<unknown>>();

function isCacheableGet(method: string, path: string): boolean {
  return method === "GET" && typeof window !== "undefined" && !NO_CACHE_RE.test(path);
}

/** Сбросить весь GET-кэш (вызывается после любой мутации; можно дёрнуть вручную). */
export function invalidateApiCache(): void {
  getCache.clear();
}

// Записи живут 8 секунд, но раньше НИКОГДА не удалялись: Map росла до
// следующей мутации, а на экранах вроде «Ассортимента» ответы весят под сотню
// килобайт — за долгую сессию это десятки мегабайт мусора в памяти вкладки.
// Подрезаем протухшее (и держим потолок) при каждой записи — дёшево и без
// таймеров.
const GET_CACHE_MAX = 120;

function pruneGetCache(): void {
  const now = Date.now();
  for (const [key, entry] of getCache) {
    if (now - entry.ts > GET_CACHE_TTL_MS) getCache.delete(key);
  }
  // Если и после чистки много (быстро листают тяжёлые экраны) — выкидываем
  // самые старые: Map сохраняет порядок вставки.
  while (getCache.size > GET_CACHE_MAX) {
    const oldest = getCache.keys().next();
    if (oldest.done) break;
    getCache.delete(oldest.value);
  }
}

async function apiFetchRaw<T>(path: string, options: RequestOptions, method: string): Promise<T> {
  const headers = new Headers(options.headers ?? {});
  if (!(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  if (options.auth !== false && typeof window !== "undefined") {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }

  // Полоса ожидания вверху (№29): считаем запросы в полёте здесь — это
  // единственная точка, через которую ходит весь портал.
  const endProgress = beginTopProgress();
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
      cache: "no-store",
    });
  } finally {
    endProgress();
  }

  if (!response.ok) {
    // Протухшая сессия: сигналим auth-provider'у (чистый выход на /login),
    // вместо россыпи «Сессия недействительна» по всем виджетам.
    if (response.status === 401 && options.auth !== false && typeof window !== "undefined") {
      window.dispatchEvent(new Event("portal:unauthorized"));
    }
    let message = "Ошибка запроса.";
    try {
      const errorData = (await response.json()) as { detail?: unknown };
      if (errorData.detail) {
        message = typeof errorData.detail === "string"
          ? errorData.detail
          : Array.isArray(errorData.detail)
            ? (errorData.detail as { msg?: string }[]).map((e) => e.msg ?? JSON.stringify(e)).join("; ")
            : JSON.stringify(errorData.detail);
      }
    } catch {
      message = response.statusText || message;
    }
    throw new Error(message);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();

  if (isCacheableGet(method, path)) {
    const cached = getCache.get(path);
    if (cached && Date.now() - cached.ts < GET_CACHE_TTL_MS) {
      return cached.data as T;
    }
    const pending = inflightGet.get(path);
    if (pending) return pending as Promise<T>;

    const exec = apiFetchRaw<T>(path, options, method);
    inflightGet.set(path, exec as Promise<unknown>);
    exec.then(
      (data) => { getCache.set(path, { ts: Date.now(), data }); pruneGetCache(); },
      () => {},
    ).finally(() => { inflightGet.delete(path); });
    return exec;
  }

  const result = await apiFetchRaw<T>(path, options, method);
  // Мутация → инвалидируем GET-кэш, чтобы последующие чтения были свежими.
  if (method !== "GET") invalidateApiCache();
  return result;
}

/**
 * Прямой доступ к транспорту api для модульных эндпоинтов без именованного
 * метода в объекте `api` (workflows, контент-завод): Bearer, нормализация
 * ошибок, GET-кэш + дедуп — как у всех. Предпочитай именованные методы;
 * НЕ заводить в компонентах собственные fetch+authHeader.
 */
export function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return apiFetch<T>(path, options);
}

export async function apiBlobFetch(path: string, options: RequestOptions = {}): Promise<Blob> {
  const headers = new Headers(options.headers ?? {});
  if (!(options.body instanceof FormData) && options.body != null && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  if (options.auth !== false && typeof window !== "undefined") {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    cache: "no-store",
  });

  if (!response.ok) {
    let message = "Ошибка запроса.";
    try {
      const errorData = (await response.json()) as { detail?: unknown };
      if (errorData.detail) {
        message = typeof errorData.detail === "string"
          ? errorData.detail
          : Array.isArray(errorData.detail)
            ? (errorData.detail as { msg?: string }[]).map((e) => e.msg ?? JSON.stringify(e)).join("; ")
            : JSON.stringify(errorData.detail);
      }
    } catch {
      message = response.statusText || message;
    }
    throw new Error(message);
  }

  return await response.blob();
}

export type PlSyncStatus = {
  status: "idle" | "running" | "done" | "cancelled" | "error";
  stage?: "init" | "analytics" | "adv" | "stocks" | "pull" | "done";
  log?: string[];
  started_at?: string;
  error?: string;
  progress?: number; // 0..1
  inner_message?: string; // живое сообщение от внутреннего sync (типа "Analytics: 2026-05-15 (15/30)...")
  pull_result?: {
    updated: number;
    stocks_pulled?: number;
    weeks_with_orders?: number;
    weeks_with_sales?: number;
  };
};

export const api = {
  login: (email: string, password: string) =>
    apiFetch<LoginResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
      auth: false,
    }),
  logout: () => apiFetch<{ status: string }>("/auth/logout", { method: "POST" }),
  session: () => apiFetch<SessionResponse>("/auth/session"),
  getTheme: () => apiFetch<ThemeConfig>("/theme", { auth: false }),
  saveTheme: (theme: ThemeConfig) => apiFetch<ThemeConfig>("/theme", { method: "PUT", body: JSON.stringify(theme) }),
  uploadLogo: (file: File, mode: "light" | "dark") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mode", mode);
    return apiFetch<{ logoUrl: string; mode: string }>("/theme/logo", { method: "POST", body: fd });
  },
  // ⚠️ Раньше ходило с { auth: false }, потому что GET /modules был публичным.
  // Теперь эндпоинт требует авторизацию (он отдаёт карту платформы: ключи всех
  // модулей и их привязку к отделам), поэтому запрос идёт с токеном.
  getModules: () => apiFetch<PortalModule[]>("/modules"),
  updateModuleDepartments: (key: string, departmentKeys: string[]) =>
    apiFetch<PortalModule>(`/modules/${encodeURIComponent(key)}`, { method: "PATCH", body: JSON.stringify({ departmentKeys }) }),
  getUsers: () => apiFetch<User[]>("/users"),
  getDepartments: () => apiFetch<Department[]>("/users/departments"),
  createDepartment: (payload: DepartmentPayload) => apiFetch<Department>("/users/departments", { method: "POST", body: JSON.stringify(payload) }),
  updateDepartment: (departmentKey: string, payload: DepartmentUpdatePayload) => apiFetch<Department>(`/users/departments/${encodeURIComponent(departmentKey)}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteDepartment: (departmentKey: string) => apiFetch<{ status: string }>(`/users/departments/${encodeURIComponent(departmentKey)}`, { method: "DELETE" }),
  reorderDepartments: (keys: string[]) => apiFetch<Department[]>("/users/departments/reorder", { method: "POST", body: JSON.stringify({ keys }) }),
  getRoles: () => apiFetch<RoleTemplate[]>("/users/roles"),
  /** Разделы базы знаний со счётчиком статей и видимостью. */
  kbCategories: () => apiFetch<import("./types").KbCategory[]>(`/kb/categories`),
  /** Описание, значок, порядок и видимость раздела. Только администратор. */
  kbSaveCategory: (name: string, body: Partial<import("./types").KbCategory>) =>
    apiFetch<{ ok: boolean }>(`/kb/categories/${encodeURIComponent(name)}`, { method: "PUT", body: JSON.stringify(body) }),
  /** Разделы, за которыми человек следит. */
  kbSubscriptions: () => apiFetch<string[]>(`/kb/subscriptions`),
  /** Статьи, отмеченные звёздочкой. Своё у каждого. */
  kbFavorites: () => apiFetch<number[]>(`/kb/favorites`),
  kbFavorite: (docId: number, on: boolean) =>
    apiFetch<{ ok: boolean; favorite: boolean }>(`/kb/favorites/${docId}`, { method: on ? "PUT" : "DELETE" }),
  kbSubscribe: (name: string, on: boolean) =>
    apiFetch<{ ok: boolean; subscribed: boolean }>(`/kb/subscriptions/${encodeURIComponent(name)}`,
      { method: on ? "PUT" : "DELETE" }),
  /** Файлы, приложенные к разделу («Связанные файлы»). */
  kbCategoryFiles: (name: string) =>
    apiFetch<import("./types").KbCategoryFile[]>(`/kb/categories/${encodeURIComponent(name)}/files`),
  kbUploadCategoryFile: (name: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch<import("./types").KbCategoryFile>(`/kb/categories/${encodeURIComponent(name)}/files`,
      { method: "POST", body: fd });
  },
  kbDeleteCategoryFile: (name: string, id: number) =>
    apiFetch<{ ok: boolean }>(`/kb/categories/${encodeURIComponent(name)}/files/${id}`, { method: "DELETE" }),
  /** Карточка коллеги: рассказ о себе и рабочие контакты. Открыта всем в портале,
   *  в отличие от полного списка учёток (он под правом «управление пользователями»). */
  publicProfile: (userId: string) =>
    apiFetch<import("./types").PublicProfile>(`/users/${encodeURIComponent(userId)}/public`),
  /** Поиск по порталу: задачи и документы базы знаний (видимость — как в самих модулях). */
  searchPortal: (q: string) =>
    apiFetch<import("./types").PortalSearch>(`/search?q=${encodeURIComponent(q)}`),
  /** Журнал выдачи и снятия доступов одного человека (новые события первыми). */
  accessLog: (userId: string) =>
    apiFetch<import("./types").AccessEvent[]>(`/users/${encodeURIComponent(userId)}/access-log`),
  createUser: (payload: UserPayload) => apiFetch<User>("/users", { method: "POST", body: JSON.stringify(payload) }),
  updateUser: (userId: string, payload: Partial<UserPayload>) =>
    apiFetch<User>(`/users/${userId}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteUser: (userId: string) => apiFetch<{ status: string }>(`/users/${userId}`, { method: "DELETE" }),
  updateMyProfile: (payload: Partial<import("@/lib/types").UserProfile>) =>
    apiFetch<User>("/users/me/profile", { method: "PATCH", body: JSON.stringify(payload) }),
  uploadMyAvatar: async (file: File): Promise<User> => {
    const form = new FormData();
    form.append("file", file);
    const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_STORAGE_KEY) : null;
    const res = await fetch(`${API_BASE}/users/me/avatar`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!res.ok) throw new Error(await res.text());
    invalidateApiCache();
    return res.json() as Promise<User>;
  },
  // Админ меняет аватар другого пользователя (требует manage_users).
  uploadUserAvatar: async (userId: string, file: File): Promise<User> => {
    const form = new FormData();
    form.append("file", file);
    const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_STORAGE_KEY) : null;
    const res = await fetch(`${API_BASE}/users/${userId}/avatar`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!res.ok) throw new Error(await res.text());
    invalidateApiCache();
    return res.json() as Promise<User>;
  },

  getLabelMakerBootstrap: () => apiFetch<LabelMakerBootstrap>("/modules/label-maker/bootstrap"),
  getLabelMakerManagedCareIcons: (includeHidden = true) => apiFetch<LabelMakerManagedCareIcon[]>(`/modules/label-maker/care-icons/manage?include_hidden=${includeHidden ? "true" : "false"}`),
  getLabelMakerCareIconFileUrl: (iconKey: string) => `${API_BASE}/modules/label-maker/care-icons/${encodeURIComponent(iconKey)}/file`,
  uploadLabelMakerCareIcon: async (file: File, name = "") => {
    const form = new FormData();
    form.append("file", file);
    if (name.trim()) form.append("name", name.trim());
    return apiFetch<LabelMakerCareIcon>("/modules/label-maker/care-icons/upload", {
      method: "POST",
      body: form,
      headers: {},
    });
  },
  deleteLabelMakerCareIcon: (iconKey: string) =>
    apiFetch<void>(`/modules/label-maker/care-icons/${encodeURIComponent(iconKey)}`, { method: "DELETE" }),
  reorderLabelMakerCareIcons: (order: string[]) =>
    apiFetch<LabelMakerManagedCareIcon[]>("/modules/label-maker/care-icons/reorder", { method: "POST", body: JSON.stringify({ order }) }),
  hideLabelMakerCareIcon: (iconKey: string) =>
    apiFetch<LabelMakerManagedCareIcon>(`/modules/label-maker/care-icons/${encodeURIComponent(iconKey)}/hide`, { method: "POST" }),
  showLabelMakerCareIcon: (iconKey: string) =>
    apiFetch<LabelMakerManagedCareIcon>(`/modules/label-maker/care-icons/${encodeURIComponent(iconKey)}/show`, { method: "POST" }),
  resetLabelMakerCareIcons: () => apiFetch<LabelMakerManagedCareIcon[]>("/modules/label-maker/care-icons/reset", { method: "POST" }),
  getLabelMakerFieldKeys: () => apiFetch<string[]>("/modules/label-maker/field-keys"),
  getLabelMakerFieldAliases: () => apiFetch<LabelMakerFieldAliasesResponse>("/modules/label-maker/field-aliases"),
  saveLabelMakerFieldAliases: (aliases: Record<string, string>) =>
    apiFetch<LabelMakerFieldAliasesResponse>("/modules/label-maker/field-aliases", { method: "PUT", body: JSON.stringify({ aliases }) }),
  resetLabelMakerFieldAliases: () => apiFetch<LabelMakerFieldAliasesResponse>("/modules/label-maker/field-aliases", { method: "DELETE" }),
  extractLabelMakerCodesFromFile: async (
    file: File,
    options: { mode?: string; limit?: number | null } = {},
  ) => {
    const form = new FormData();
    form.append("file", file);
    const query = new URLSearchParams();
    if (options.mode) query.set("mode", options.mode);
    if (typeof options.limit === "number" && Number.isFinite(options.limit) && options.limit > 0) {
      query.set("limit", String(Math.floor(options.limit)));
    }
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return apiFetch<LabelMakerCodesExtractResult>(`/modules/label-maker/codes/extract${suffix}`, {
      method: "POST",
      body: form,
      headers: {},
    });
  },
  extractLabelMakerCodesStream: async (
    file: File,
    options: { mode?: string; limit?: number | null } = {},
  ) => {
    const form = new FormData();
    form.append("file", file);
    const query = new URLSearchParams();
    if (options.mode) query.set("mode", options.mode);
    if (typeof options.limit === "number" && Number.isFinite(options.limit) && options.limit > 0) {
      query.set("limit", String(Math.floor(options.limit)));
    }
    const headers = new Headers();
    if (typeof window !== "undefined") {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (token) headers.set("Authorization", `Bearer ${token}`);
    }
    const suffix = query.toString() ? `?${query.toString()}` : "";
    const response = await fetch(`${API_BASE}/modules/label-maker/codes/extract-stream${suffix}`, {
      method: "POST",
      body: form,
      headers,
      cache: "no-store",
    });
    if (!response.ok) {
      let message = "Ошибка запроса.";
      try {
        const errorData = (await response.json()) as { detail?: string };
        if (errorData.detail) message = errorData.detail;
      } catch {
        message = response.statusText || message;
      }
      throw new Error(message);
    }
    return response;
  },
  roundtripLabelMakerDataMatrix: (code: string) =>
    apiFetch<LabelMakerDmRoundtripResponse>("/modules/label-maker/datamatrix/roundtrip", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  getLabelMakerProducts: () => apiFetch<LabelMakerProduct[]>("/modules/label-maker/products"),
  createLabelMakerProduct: (payload: LabelMakerProductPayload) =>
    apiFetch<LabelMakerProduct>("/modules/label-maker/products", { method: "POST", body: JSON.stringify(payload) }),
  updateLabelMakerProduct: (productId: string, payload: Partial<LabelMakerProductPayload>) =>
    apiFetch<LabelMakerProduct>(`/modules/label-maker/products/${productId}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteLabelMakerProduct: (productId: string) =>
    apiFetch<void>(`/modules/label-maker/products/${productId}`, { method: "DELETE" }),
  bulkDeleteLabelMakerProducts: (ids: string[]) =>
    apiFetch<LabelMakerBulkDeleteResponse>("/modules/label-maker/products/bulk-delete", { method: "POST", body: JSON.stringify({ ids }) }),
  importLabelMakerProductsXlsx: async (file: File, upsert = true) => {
    const form = new FormData();
    form.append("file", file);
    const query = new URLSearchParams({ upsert: upsert ? "true" : "false" }).toString();
    return apiFetch<LabelMakerImportResult>(`/modules/label-maker/products/import-xlsx?${query}`, {
      method: "POST",
      body: form,
      headers: {},
    });
  },
  getLabelMakerWbCabinets: () => apiFetch<LabelMakerWbCabinet[]>("/modules/label-maker/wb-cabinets"),
  createLabelMakerWbCabinet: (payload: LabelMakerWbCabinetPayload) =>
    apiFetch<LabelMakerWbCabinet>("/modules/label-maker/wb-cabinets", { method: "POST", body: JSON.stringify(payload) }),
  updateLabelMakerWbCabinet: (cabinetId: string, payload: LabelMakerWbCabinetPayload) =>
    apiFetch<LabelMakerWbCabinet>(`/modules/label-maker/wb-cabinets/${cabinetId}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteLabelMakerWbCabinet: (cabinetId: string) =>
    apiFetch<void>(`/modules/label-maker/wb-cabinets/${cabinetId}`, { method: "DELETE" }),
  importLabelMakerWbCards: (payload: { cabinetIds: string[]; upsert: boolean; limit: number; maxPages: number; sleepMs: number; locale: string }) =>
    apiFetch<LabelMakerWbImportResult>("/modules/label-maker/wb-cabinets/import-cards", { method: "POST", body: JSON.stringify(payload) }),
  getLabelMakerTemplates: () => apiFetch<LabelMakerTemplate[]>("/modules/label-maker/templates"),
  createLabelMakerTemplate: (payload: LabelMakerTemplatePayload) =>
    apiFetch<LabelMakerTemplate>("/modules/label-maker/templates", { method: "POST", body: JSON.stringify(payload) }),
  updateLabelMakerTemplate: (templateId: string, payload: LabelMakerTemplatePayload) =>
    apiFetch<LabelMakerTemplate>(`/modules/label-maker/templates/${templateId}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteLabelMakerTemplate: (templateId: string) =>
    apiFetch<void>(`/modules/label-maker/templates/${templateId}`, { method: "DELETE" }),
  exportLabelMakerTemplates: (ids: string[] = []) =>
    apiBlobFetch("/modules/label-maker/templates/export", { method: "POST", body: JSON.stringify({ ids }) }),
  importLabelMakerTemplates: async (file: File, upsert = true) => {
    const form = new FormData();
    form.append("file", file);
    const query = new URLSearchParams({ upsert: upsert ? "true" : "false" }).toString();
    return apiFetch<LabelMakerTemplateTransferResult>(`/modules/label-maker/templates/import?${query}`, {
      method: "POST",
      body: form,
      headers: {},
    });
  },
  previewLabelMakerTemplate: (payload: { productId: string; template: LabelMakerTemplatePayload }) =>
    apiBlobFetch("/modules/label-maker/templates/preview", { method: "POST", body: JSON.stringify(payload) }),
  previewLabelMakerTemplateImage: (payload: { productId: string; template: LabelMakerTemplatePayload }) =>
    apiBlobFetch("/modules/label-maker/templates/preview-image", { method: "POST", body: JSON.stringify(payload) }),
  renderLabelMakerTemplate: (payload: { productId: string; templateId: string; quantity: number }) =>
    apiBlobFetch("/modules/label-maker/templates/render", { method: "POST", body: JSON.stringify(payload) }),
  startLabelMakerRenderJob: (payload: { productId: string; templateId: string; codes: string[] }) =>
    apiFetch<LabelMakerRenderJobStartResponse>("/modules/label-maker/templates/render-job", { method: "POST", body: JSON.stringify(payload) }),
  getLabelMakerRenderJob: (jobId: string) =>
    apiFetch<LabelMakerRenderJobStatus>(`/modules/label-maker/jobs/${jobId}`),
  downloadLabelMakerRenderJob: (jobId: string) =>
    apiBlobFetch(`/modules/label-maker/jobs/${jobId}/download`),

  getRoadmapBoard: () => apiFetch<RoadmapBoard>("/modules/roadmap/board"),
  saveRoadmapBoard: (payload: RoadmapBoardPayload) =>
    apiFetch<RoadmapBoard>("/modules/roadmap/board", { method: "PUT", body: JSON.stringify(payload) }),
  createRoadmapSuggestion: (payload: { title: string; description?: string; urgency: string; tags?: string[] }) =>
    apiFetch<RoadmapSuggestion>("/modules/roadmap/suggestions", { method: "POST", body: JSON.stringify(payload) }),
  updateRoadmapSuggestion: (id: string, payload: { status?: string; title?: string; description?: string }) =>
    apiFetch<RoadmapSuggestion>(`/modules/roadmap/suggestions/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteRoadmapSuggestion: (id: string) =>
    apiFetch<{ ok: boolean }>(`/modules/roadmap/suggestions/${id}`, { method: "DELETE" }),
  moveRoadmapSuggestion: (id: string, column_key: string) =>
    apiFetch<RoadmapBoard>(`/modules/roadmap/suggestions/${id}/move`, { method: "POST", body: JSON.stringify({ column_key }) }),

  getWbSummary: () => apiFetch<WbReviewSummary>("/modules/wb-reviews/summary"),
  getWbCabinets: () => apiFetch<WbReviewCabinet[]>("/modules/wb-reviews/cabinets"),
  updateWbCabinet: (cabinetId: string, payload: Partial<WbReviewCabinetPayload>) =>
    apiFetch<WbReviewCabinet>(`/modules/wb-reviews/cabinets/${cabinetId}`, { method: "PATCH", body: JSON.stringify(payload) }),
  validateWbCabinet: (cabinetId: string) => apiFetch<WbReviewCabinet>(`/modules/wb-reviews/cabinets/${cabinetId}/validate`, { method: "POST" }),
  syncWbCabinet: (cabinetId: string) => apiFetch<WbReviewCabinet>(`/modules/wb-reviews/cabinets/${cabinetId}/sync`, { method: "POST" }),
  cancelWbCabinetSync: (cabinetId: string) => apiFetch<WbReviewCabinet>(`/modules/wb-reviews/cabinets/${cabinetId}/sync/cancel`, { method: "POST" }),
  getWbCabinetSyncStatus: (cabinetId: string) => apiFetch<WbReviewCabinet>(`/modules/wb-reviews/cabinets/${cabinetId}/sync-status`),
  deleteWbCabinet: (cabinetId: string) => apiFetch<void>(`/modules/wb-reviews/cabinets/${cabinetId}`, { method: "DELETE" }),
  getWbReviews: (params: WbReviewFilters = {}) => {
    const searchParams = new URLSearchParams();
    if (params.cabinetId && params.cabinetId !== "all") searchParams.set("cabinet_id", params.cabinetId);
    if (params.status && params.status !== "all") searchParams.set("status", params.status);
    if (params.scenarioCode && params.scenarioCode !== "all") searchParams.set("scenario_code", params.scenarioCode);
    if (params.rating && params.rating !== "all") searchParams.set("rating", params.rating);
    if (params.wbStatus && params.wbStatus !== "all") searchParams.set("wb_status", params.wbStatus);
    const search = normalizeQueryValue(params.search);
    if (search) searchParams.set("search", search);
    if (params.limit) searchParams.set("limit", String(params.limit));
    if (typeof params.offset === "number") searchParams.set("offset", String(params.offset));
    if (params.sortBy) searchParams.set("sort_by", params.sortBy);
    if (params.sortDir) searchParams.set("sort_dir", params.sortDir);
    const suffix = searchParams.toString() ? `?${searchParams.toString()}` : "";
    return apiFetch<WbReviewListResponse>(`/modules/wb-reviews/reviews${suffix}`);
  },
  regenerateWbReview: (reviewId: string) =>
    apiFetch<{ review: WbReview; message: string }>(`/modules/wb-reviews/reviews/${reviewId}/generate`, { method: "POST" }),
  publishWbReview: (reviewId: string) =>
    apiFetch<{ review: WbReview; message: string }>(`/modules/wb-reviews/reviews/${reviewId}/publish`, { method: "POST" }),
  deleteWbReviewDraft: (reviewId: string) =>
    apiFetch<{ review: WbReview; message: string }>(`/modules/wb-reviews/reviews/${reviewId}/draft`, { method: "DELETE" }),
  bulkRegenerateWbReviews: (reviewIds: string[]) =>
    apiFetch<WbReviewBulkPublishJobStatus>(`/modules/wb-reviews/reviews/bulk-generate`, { method: "POST", body: JSON.stringify({ reviewIds }) }),
  bulkPublishWbReviews: (reviewIds: string[]) =>
    apiFetch<WbReviewBulkPublishJobStatus>(`/modules/wb-reviews/reviews/bulk-publish`, { method: "POST", body: JSON.stringify({ reviewIds }) }),
  getWbBulkJobStatus: (jobId: string) =>
    apiFetch<WbReviewBulkPublishJobStatus>(`/modules/wb-reviews/reviews/bulk-jobs/${jobId}`),
  getWbBulkJobs: (limit = 8) =>
    apiFetch<WbReviewBulkPublishJobStatus[]>(`/modules/wb-reviews/reviews/bulk-jobs?limit=${limit}`),
  bulkDeleteWbReviewDrafts: (reviewIds: string[]) =>
    apiFetch<WbReviewBulkPublishJobStatus>(`/modules/wb-reviews/reviews/bulk-delete-drafts`, { method: "POST", body: JSON.stringify({ reviewIds }) }),
  bulkRegenerateWbReviewsByFilter: (payload: { cabinetId?: string; status?: string; scenarioCode?: string; rating?: number | null; wbStatus?: string; search?: string }) =>
    apiFetch<WbReviewBulkPublishJobStatus>(`/modules/wb-reviews/reviews/bulk-generate-by-filter`, { method: "POST", body: JSON.stringify(payload) }),
  bulkDeleteWbReviewDraftsByFilter: (payload: { cabinetId?: string; status?: string; scenarioCode?: string; rating?: number | null; wbStatus?: string; search?: string }) =>
    apiFetch<WbReviewBulkPublishJobStatus>(`/modules/wb-reviews/reviews/bulk-delete-drafts-by-filter`, { method: "POST", body: JSON.stringify(payload) }),
  bulkPublishWbReviewsByFilter: (payload: { cabinetId?: string; status?: string; scenarioCode?: string; rating?: number | null; wbStatus?: string; search?: string }) =>
    apiFetch<WbReviewBulkPublishJobStatus>(`/modules/wb-reviews/reviews/bulk-publish-by-filter`, { method: "POST", body: JSON.stringify(payload) }),
  getWbReviewSettings: () => apiFetch<WbReviewSettings>("/modules/wb-reviews/settings"),
  saveWbReviewSettings: (payload: WbReviewSettings) =>
    apiFetch<WbReviewSettings>("/modules/wb-reviews/settings", { method: "PUT", body: JSON.stringify(payload) }),

  // ── Telegram Bot Platform ──────────────────────────────────────────────────
  getTgBots: () => apiFetch<TgBot[]>("/telegram/bots"),
  createTgBot: (payload: { name: string; token: string; username?: string; description?: string; is_active?: boolean }) =>
    apiFetch<TgBot>("/telegram/bots", { method: "POST", body: JSON.stringify(payload) }),
  deleteTgBot: (botId: number) => apiFetch<{ ok: boolean }>(`/telegram/bots/${botId}`, { method: "DELETE" }),
  testTgBot: (botId: number) => apiFetch<{ ok: boolean; bot: Record<string, unknown> }>(`/telegram/bots/${botId}/test`, { method: "POST" }),
  setTgWebhook: (botId: number) => apiFetch<{ ok: boolean; url: string }>(`/telegram/bots/${botId}/set-webhook`, { method: "POST" }),
  getTgFlows: () => apiFetch<TgFlow[]>("/telegram/flows"),
  createTgFlow: (payload: { bot_id: number; name: string; description?: string; is_default?: boolean }) =>
    apiFetch<{ ok: boolean; id: number }>("/telegram/flows", { method: "POST", body: JSON.stringify(payload) }),
  getTgFlow: (flowId: number) => apiFetch<TgFlowDetail>(`/telegram/flows/${flowId}`),
  saveTgFlow: (flowId: number, draft: { nodes: unknown[]; edges: unknown[] }) =>
    apiFetch<{ ok: boolean }>(`/telegram/flows/${flowId}`, { method: "PUT", body: JSON.stringify({ draft }) }),
  publishTgFlow: (flowId: number) => apiFetch<{ ok: boolean }>(`/telegram/flows/${flowId}/publish`, { method: "POST" }),
  deleteTgFlow: (flowId: number) => apiFetch<{ ok: boolean }>(`/telegram/flows/${flowId}`, { method: "DELETE" }),
  uploadTgFlowMedia: async (flowId: number, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return apiFetch<{ ok: boolean; url: string; relative_url: string; filename: string }>(
      `/telegram/flows/${flowId}/media-upload`, { method: "POST", body: form, headers: {} }
    );
  },

  getTgApplicationNodes: () => apiFetch<TgNodeGroup[]>("/telegram/applications/nodes"),
  getTgApplicationsForNode: (flowId: number | null, nodeId: string) => {
    const params = new URLSearchParams({ node_id: nodeId });
    if (flowId !== null) params.set("flow_id", String(flowId));
    return apiFetch<TgApplication[]>(`/telegram/applications/node?${params}`);
  },
  moderateTgApplication: (id: number, status: string, comment?: string) =>
    apiFetch<{ ok: boolean }>(`/telegram/applications/${id}/moderate`, { method: "POST", body: JSON.stringify({ status, comment }) }),
  bulkDeleteTgApplications: (ids: number[]) =>
    apiFetch<{ ok: boolean; deleted_ids: number[] }>("/telegram/applications/bulk-delete", { method: "POST", body: JSON.stringify({ ids }) }),

  getTgCareNodes: () => apiFetch<TgNodeGroup[]>("/telegram/care/nodes"),
  getTgCareTicketsForNode: (flowId: number | null, nodeId: string) => {
    const params = new URLSearchParams({ node_id: nodeId });
    if (flowId !== null) params.set("flow_id", String(flowId));
    return apiFetch<TgCareTicket[]>(`/telegram/care/node?${params}`);
  },
  replyTgCareTicket: (id: number, message: string) =>
    apiFetch<{ ok: boolean; item?: TgCareTicket }>(`/telegram/care/${id}/reply`, { method: "POST", body: JSON.stringify({ message }) }),
  bulkDeleteTgCareTickets: (ids: number[]) =>
    apiFetch<{ ok: boolean; deleted_ids: number[] }>("/telegram/care/bulk-delete", { method: "POST", body: JSON.stringify({ ids }) }),

  getTgRaffles: () => apiFetch<TgRaffleEntry[]>("/telegram/raffles"),
  awardTgRaffle: (id: number, message: string) =>
    apiFetch<{ ok: boolean }>(`/telegram/raffles/${id}/award`, { method: "POST", body: JSON.stringify({ message }) }),

  getTgPromos: () => apiFetch<TgPromoPool[]>("/telegram/promos"),
  getTgPromoPool: (id: number) => apiFetch<TgPromoPool & { codes: TgPromoCode[] }>(`/telegram/promos/${id}`),
  createTgPromoPool: (payload: { bot_id: number; name: string; description?: string; codes?: string[] }) =>
    apiFetch<{ ok: boolean; id: number }>("/telegram/promos", { method: "POST", body: JSON.stringify(payload) }),
  updateTgPromoPool: (id: number, payload: { name?: string; description?: string }) =>
    apiFetch<TgPromoPool>(`/telegram/promos/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  addTgPromoCodes: (poolId: number, codes: string[], maxUses?: number | null) =>
    apiFetch<{ ok: boolean; added: number }>(`/telegram/promos/${poolId}/codes`, { method: "POST", body: JSON.stringify({ codes, max_uses: maxUses ?? null }) }),
  exportTgApplications: (params?: { flow_id?: number; node_id?: string }) => {
    const q = new URLSearchParams();
    if (params?.flow_id) q.set("flow_id", String(params.flow_id));
    if (params?.node_id) q.set("node_id", params.node_id);
    return apiBlobFetch(`/telegram/applications/export?${q}`);
  },
  exportTgCare: (params?: { flow_id?: number; node_id?: string }) => {
    const q = new URLSearchParams();
    if (params?.flow_id) q.set("flow_id", String(params.flow_id));
    if (params?.node_id) q.set("node_id", params.node_id);
    return apiBlobFetch(`/telegram/care/export?${q}`);
  },
  updateTgPromoCode: (poolId: number, codeId: number, payload: { max_uses: number | null }) =>
    apiFetch<{ id: number; max_uses: number | null; is_issued: boolean }>(`/telegram/promos/${poolId}/codes/${codeId}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteTgPromoCode: (poolId: number, codeId: number) =>
    apiFetch<{ ok: boolean }>(`/telegram/promos/${poolId}/codes/${codeId}`, { method: "DELETE" }),
  deleteTgPromoPool: (id: number) => apiFetch<{ ok: boolean }>(`/telegram/promos/${id}`, { method: "DELETE" }),

  getTgBroadcasts: () => apiFetch<TgBroadcast[]>("/telegram/broadcasts"),
  createTgBroadcast: (payload: { bot_id: number; title: string; text: string; media_url?: string; media_type?: string; scheduled_at?: string; tags?: string[] }) =>
    apiFetch<{ ok: boolean; id: number }>("/telegram/broadcasts", { method: "POST", body: JSON.stringify(payload) }),
  updateTgBroadcast: (id: number, payload: { title?: string; text?: string; media_url?: string; media_type?: string; scheduled_at?: string; tags?: string[] }) =>
    apiFetch<TgBroadcast>(`/telegram/broadcasts/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  runTgBroadcast: (id: number) => apiFetch<{ ok: boolean; sent: number; failed: number }>(`/telegram/broadcasts/${id}/run`, { method: "POST" }),
  deleteTgBroadcast: (id: number) => apiFetch<{ ok: boolean }>(`/telegram/broadcasts/${id}`, { method: "DELETE" }),

  getTgUsers: () => apiFetch<TgUser[]>("/telegram/users"),
  deleteTgUser: (id: number) => apiFetch<{ ok: boolean }>(`/telegram/users/${id}`, { method: "DELETE" }),

  // ── WB Analytics ──────────────────────────────────────────────────────────
  getWbAnalyticsCabinets: () => apiFetch<WbAnalyticsCabinet[]>("/modules/sales/cabinets"),
  updateWbAnalyticsCabinet: (cabinetId: string, payload: Partial<WbAnalyticsCabinetPayload>) =>
    apiFetch<WbAnalyticsCabinet>(`/modules/sales/cabinets/${cabinetId}`, { method: "PATCH", body: JSON.stringify(payload) }),
  clearWbAnalyticsCabinetData: (cabinetId: string) =>
    apiFetch<WbAnalyticsCabinet>(`/modules/sales/cabinets/${cabinetId}/clear-data`, { method: "POST" }),
  validateWbAnalyticsCabinet: (cabinetId: string) =>
    apiFetch<{ status: string; message: string }>(`/modules/sales/cabinets/${cabinetId}/validate`, { method: "POST" }),
  syncWbAnalyticsAdv: (cabinetId: string, dateFrom: string, dateTo: string) =>
    apiFetch<{ datesStored: number; totalSum: number; dateFrom: string; dateTo: string; log: string[] }>(
      `/modules/sales/cabinets/${cabinetId}/sync-adv`,
      { method: "POST", body: JSON.stringify({ dateFrom, dateTo }) }
    ),
  startWbAnalyticsSync: (cabinetId: string, dateFrom: string, dateTo: string) =>
    apiFetch<WbAnalyticsCabinet>(`/modules/sales/cabinets/${cabinetId}/sync`, { method: "POST", body: JSON.stringify({ dateFrom, dateTo }) }),
  cancelWbAnalyticsSync: (cabinetId: string) =>
    apiFetch<WbAnalyticsCabinet>(`/modules/sales/cabinets/${cabinetId}/sync/cancel`, { method: "POST" }),
  resetWbAnalyticsPendingChunks: (cabinetId: string) =>
    apiFetch<{ deleted: number }>(`/modules/sales/cabinets/${cabinetId}/sync/reset-pending`, { method: "POST" }),
  getWbAnalyticsWeekly: (cabinetId: string, limit = 0, dateMode: "settlement" | "order" = "settlement", real = false) =>
    apiFetch<WbAnalyticsWeekRow[]>(`/modules/sales/cabinets/${cabinetId}/weekly?limit=${limit}&date_mode=${dateMode}${real ? "&real=1" : ""}`),
  syncWbAnalyticsHistorical: (cabinetId: string, dateFrom: string, dateTo: string) =>
    apiFetch<WbAnalyticsCabinet>(`/modules/sales/cabinets/${cabinetId}/sync-historical`, {
      method: "POST",
      body: JSON.stringify({ dateFrom, dateTo }),
    }),
  importWbAnalyticsWeekly: (cabinetId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return apiFetch<{ inserted: number; updated: number; skipped: number; errors: string[] }>(
      `/modules/sales/cabinets/${cabinetId}/import-weekly`,
      { method: "POST", body: form },
    );
  },
  downloadWbAnalyticsWeeklyTemplate: (cabinetId: string) =>
    apiBlobFetch(`/modules/sales/cabinets/${cabinetId}/weekly-template`),
  getSalesReport: (params: { date?: string; periodFrom?: string; periodTo?: string } = {}, real = false) => {
    const q = new URLSearchParams();
    if (params.date) q.set("date", params.date);
    if (params.periodFrom) q.set("period_from", params.periodFrom);
    if (params.periodTo) q.set("period_to", params.periodTo);
    if (real) q.set("real", "1");
    const qs = q.toString();
    return apiFetch<SalesReport>(`/modules/sales/sales-report${qs ? `?${qs}` : ""}`);
  },
  /** Дневной ГПР одной страницей PDF — менеджер шлёт его в чат с комментариями. */
  getSalesReportPdf: (params: { date?: string; periodFrom?: string; periodTo?: string } = {}, real = false) => {
    const q = new URLSearchParams();
    if (params.date) q.set("date", params.date);
    if (params.periodFrom) q.set("period_from", params.periodFrom);
    if (params.periodTo) q.set("period_to", params.periodTo);
    if (real) q.set("real", "1");
    const qs = q.toString();
    return apiBlobFetch(`/modules/sales/sales-report/pdf${qs ? `?${qs}` : ""}`);
  },
  getWbAnalyticsArticles: (cabinetId: string) =>
    apiFetch<WbAnalyticsArticle[]>(`/modules/sales/cabinets/${cabinetId}/articles`),
  upsertWbAnalyticsCostPrice: (cabinetId: string, payload: { saName: string; costPrice: number; nmId?: number | null; markirovka?: number; logisticMp?: number; storage?: number; priemka?: number; sppPct?: number; wbCommissionPct?: number; lengthCm?: number; widthCm?: number; heightCm?: number; additionalCosts?: number; vatPct?: number; targetMarg?: number; buyoutSpecialPct?: number; commissionCorrectionPct?: number; priemkaBasePerLiter?: number; targetMargWithDrr?: number; targetDrr?: number; realCostPrice?: number }) =>
    apiFetch<WbAnalyticsCostPrice>(`/modules/sales/cabinets/${cabinetId}/cost-prices`, { method: "PUT", body: JSON.stringify(payload) }),
  importWbAnalyticsCostPricesXls: (cabinetId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return apiFetch<{ imported: number; total: number; errors: string[] }>(
      `/modules/sales/cabinets/${cabinetId}/cost-prices/import-xls`,
      { method: "POST", body: form },
    );
  },
  getWbAnalyticsStocks: (cabinetId: string, source: "analytics" | "financial" = "analytics") =>
    apiFetch<WbAnalyticsStockItem[]>(`/modules/sales/cabinets/${cabinetId}/stocks?source=${source}`),
  syncWbAnalyticsStocks: (cabinetId: string) =>
    apiFetch<{ status: string }>(`/modules/sales/cabinets/${cabinetId}/sync-stocks`, { method: "POST" }),
  getWbAnalyticsStockSyncStatus: (cabinetId: string) =>
    apiFetch<{ status: string; message?: string }>(`/modules/sales/cabinets/${cabinetId}/sync-stocks/status`),
  // Остатки ПО СКЛАДАМ (вкладка «Остатки по складам» отдела Продажи): суточный
  // снимок warehouse_remains в разрезе складов + общий список «учитывать склад».
  wbStockByWarehouse: (cabinetId: string, date?: string) =>
    apiFetch<import("@/lib/types").WbWarehouseStockData>(
      `/modules/sales/cabinets/${cabinetId}/stock-by-warehouse${date ? `?date=${date}` : ""}`,
    ),
  wbStockByWarehouseXlsx: (cabinetId: string, date?: string) =>
    apiBlobFetch(`/modules/sales/cabinets/${cabinetId}/stock-by-warehouse/export${date ? `?date=${date}` : ""}`),
  wbSetWarehouseFlag: (payload: { warehouse: string; enabled: boolean; note?: string }) =>
    apiFetch<{ warehouse: string; enabled: boolean; note: string; updatedAt: string }>(
      "/modules/sales/warehouse-flags",
      { method: "PUT", body: JSON.stringify(payload) },
    ),
  getWbUnitEconomics: (cabinetId: string, weeks = 4, dateFrom = "", dateTo = "", real = false) =>
    apiFetch<import("@/lib/types").WbUnitEconomicsRow[]>(
      `/modules/sales/cabinets/${cabinetId}/unit-economics?weeks=${weeks}` +
      (dateTo ? `&date=${dateTo}` : "") + (dateFrom ? `&date_from=${dateFrom}` : "") + (real ? "&real=1" : "")),
  getWbUnitEconomicsSnapshot: (cabinetId: string, date = "", real = false) =>
    apiFetch<import("@/lib/types").WbUnitEconomicsRow[]>(`/modules/sales/cabinets/${cabinetId}/unit-economics/snapshot?${date ? `date=${date}&` : ""}${real ? "real=1" : ""}`),
  // dateFrom/dateTo — общий период экрана; без них бэкенд возьмёт `weeks` недель.
  getWbConsolidatedChecklist: (cabinetId: string, weeks = 4, real = false, dateFrom?: string, dateTo?: string) => {
    const range = dateFrom && dateTo
      ? `&date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}` : "";
    return apiFetch<import("@/lib/types").WbConsolidatedRow[]>(
      `/modules/sales/cabinets/${cabinetId}/consolidated-checklist?weeks=${weeks}${real ? "&real=1" : ""}${range}`,
    );
  },
  getWbChecklistSkus: (cabinetId: string, real = false) =>
    apiFetch<import("@/lib/types").WbChecklistSku[]>(`/modules/sales/cabinets/${cabinetId}/checklist/skus${real ? "?real=1" : ""}`),
  getWbChecklist: (cabinetId: string, nmId: number, dateFrom: string, dateTo: string, real = false) =>
    apiFetch<import("@/lib/types").WbChecklistData>(`/modules/sales/cabinets/${cabinetId}/checklist?nm_id=${nmId}&date_from=${dateFrom}&date_to=${dateTo}${real ? "&real=1" : ""}`),
  getWbChecklistJam: (cabinetId: string, nmId: number, real = false) =>
    apiFetch<import("@/lib/types").WbChecklistJam>(`/modules/sales/cabinets/${cabinetId}/checklist/jam?nm_id=${nmId}${real ? "&real=1" : ""}`),
  setWbChecklistSearchShare: (cabinetId: string, nmId: number, searchPct: number | null) =>
    apiFetch<void>(`/modules/sales/cabinets/${cabinetId}/checklist/search-share?nm_id=${nmId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ search_pct: searchPct }),
    }),
  getWbUnitHistory: (cabinetId: string, days = 60, saName = "") =>
    apiFetch<import("@/lib/types").WbUnitHistoryPoint[]>(`/modules/sales/cabinets/${cabinetId}/unit-history?days=${days}${saName ? `&sa_name=${encodeURIComponent(saName)}` : ""}`),
  setWbArticleArchived: (cabinetId: string, saName: string, isArchived: boolean) =>
    apiFetch<{ ok: boolean }>(`/modules/sales/cabinets/${cabinetId}/articles/archive`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sa_name: saName, is_archived: isArchived }),
    }),
  getWbAnalyticsArticleChart: (cabinetId: string, saName: string, techSize = "", dateMode: "settlement" | "order" = "settlement") =>
    apiFetch<WbAnalyticsArticleWeekRow[]>(`/modules/sales/cabinets/${cabinetId}/article-chart?sa_name=${encodeURIComponent(saName)}&tech_size=${encodeURIComponent(techSize)}&date_mode=${dateMode}`),
  /** Ручные расходы ОПиУ (постоянные, кредиты, дивиденды) — общий список кабинета:
   *  их правят и в ОПиУ 10X, и в ОПиУ (mppulse), таблица одна. */
  listWbExtraCosts: (cabinetId: string) =>
    apiFetch<WbExtraCost[]>(`/modules/sales/cabinets/${cabinetId}/extra-costs`),
  upsertWbExtraCost: (cabinetId: string, yearMonth: string, category: string, amount: number, notes?: string) =>
    apiFetch<WbExtraCost>(`/modules/sales/cabinets/${cabinetId}/extra-costs/${yearMonth}/${category}`, {
      method: "PUT", body: JSON.stringify({ amount, notes: notes ?? "" }),
    }),
  // Раздачи и внешний трафик — по товару и дате (10X вводит их именно так,
  // иначе они не попадают в цену заказа конкретного дня).
  getWbPromoCosts: (cabinetId: string, dateFrom?: string, dateTo?: string, nmId?: number) => {
    const qs = new URLSearchParams();
    if (dateFrom) qs.set("date_from", dateFrom);
    if (dateTo) qs.set("date_to", dateTo);
    if (nmId) qs.set("nm_id", String(nmId));
    const tail = qs.toString();
    return apiFetch<import("@/lib/types").WbPromoCost[]>(
      `/modules/sales/cabinets/${cabinetId}/promo-costs${tail ? `?${tail}` : ""}`,
    );
  },
  saveWbPromoCost: (
    cabinetId: string,
    payload: {
      id?: string; date: string; nm_id: number; kind: "giveaway" | "external";
      amount: number; sa_name?: string; qty?: number; source?: string; notes?: string;
    },
  ) => {
    const { id, ...body } = payload;
    return apiFetch<import("@/lib/types").WbPromoCost>(
      `/modules/sales/cabinets/${cabinetId}/promo-costs${id ? `/${id}` : ""}`,
      { method: id ? "PUT" : "POST", body: JSON.stringify(body) },
    );
  },
  deleteWbPromoCost: (cabinetId: string, rowId: string) =>
    apiFetch<void>(`/modules/sales/cabinets/${cabinetId}/promo-costs/${rowId}`, { method: "DELETE" }),
  upsertWbAnalyticsSeasonality: (cabinetId: string, payload: { weekStart: string; label: string; coefficient: number }) =>
    apiFetch<WbSeasonality>(`/modules/sales/cabinets/${cabinetId}/seasonality`, { method: "PUT", body: JSON.stringify(payload) }),
  syncWbFunnel: (cabinetId: string, dateFrom: string, dateTo: string) =>
    apiFetch<{ status: string }>(`/modules/sales/cabinets/${cabinetId}/funnel/sync`, { method: "POST", body: JSON.stringify({ dateFrom, dateTo }) }),
  getWbFunnelSyncStatus: (cabinetId: string) =>
    apiFetch<{ status: string; message: string; synced: number }>(`/modules/sales/cabinets/${cabinetId}/funnel/sync-status`),
  getWbFunnel: (cabinetId: string, dateFrom: string, dateTo: string) =>
    apiFetch<WbFunnelRow[]>(`/modules/sales/cabinets/${cabinetId}/funnel?date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}`),
  syncWbPaidStorage: (cabinetId: string, dateFrom: string, dateTo: string) =>
    apiFetch<{ status: string }>(`/modules/sales/cabinets/${cabinetId}/paid-storage/sync`, { method: "POST", body: JSON.stringify({ dateFrom, dateTo }) }),
  setWbAutoSync: (cabinetId: string, settings: { enabled: boolean; hourUtc: number; lookbackDays: number; syncAdv: boolean }) =>
    apiFetch<WbAnalyticsCabinet>(`/modules/sales/cabinets/${cabinetId}/auto-sync`, { method: "PUT", body: JSON.stringify({ enabled: settings.enabled, hourUtc: settings.hourUtc, lookbackDays: settings.lookbackDays, syncAdv: settings.syncAdv }) }),
  autofillWbCommissions: (cabinetId: string) =>
    apiFetch<{ updated: number; created: number; unmatched: number; totalTariffs: number }>(`/modules/sales/cabinets/${cabinetId}/autofill/commissions`, { method: "POST" }),
  autofillLogisticMp: (cabinetId: string) =>
    apiFetch<{ updated: number; skipped: number; tariffWarehouse: string; tariffBase: number; tariffPerLiter: number; contentError: string | null }>(`/modules/sales/cabinets/${cabinetId}/autofill/logistic-mp`, { method: "POST" }),
  getWbSkuPl: (cabinetId: string, dateFrom: string, dateTo: string, real = false) =>
    apiFetch<WbSkuPlRow[]>(`/modules/sales/cabinets/${cabinetId}/sku-pl?date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}${real ? "&real=1" : ""}`),
  getWbMppulseDashboard: (
    cabinetId: string,
    startDate: string,
    endDate: string,
    opts?: { subjects?: string[]; nmIds?: number[]; granularity?: "day" | "week" | "month"; real?: boolean },
  ) => {
    const qs = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (opts?.subjects?.length) qs.set("subjects", opts.subjects.join(","));
    if (opts?.nmIds?.length) qs.set("nm_ids", opts.nmIds.join(","));
    if (opts?.granularity) qs.set("granularity", opts.granularity);
    if (opts?.real) qs.set("real", "1");
    return apiFetch<MppulseDashboard>(
      `/modules/sales/cabinets/${cabinetId}/mppulse-dashboard?${qs.toString()}`,
    );
  },

  getWbAdvAnalytics: (cabinetId: string, startDate: string, endDate: string) => {
    const qs = new URLSearchParams({ start_date: startDate, end_date: endDate });
    return apiFetch<AdvAnalytics>(
      `/modules/sales/cabinets/${cabinetId}/adv-analytics?${qs.toString()}`,
    );
  },
  getWbRnpPulse: (
    cabinetId: string,
    startDate: string,
    endDate: string,
    opts?: { bySku?: boolean; subjects?: string[]; nmIds?: number[]; real?: boolean },
  ) => {
    const qs = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (opts?.bySku) qs.set("by_sku", "true");
    if (opts?.subjects?.length) qs.set("subjects", opts.subjects.join(","));
    if (opts?.nmIds?.length) qs.set("nm_ids", opts.nmIds.join(","));
    if (opts?.real) qs.set("real", "1");
    return apiFetch<RnpPulse>(
      `/modules/sales/cabinets/${cabinetId}/rnp-pulse?${qs.toString()}`,
    );
  },
  getWbPnlOpiy: (
    cabinetId: string,
    months: number,
    opts?: { subjects?: string[]; nmIds?: number[]; real?: boolean },
  ) => {
    const qs = new URLSearchParams({ months: String(months) });
    if (opts?.subjects?.length) qs.set("subjects", opts.subjects.join(","));
    if (opts?.nmIds?.length) qs.set("nm_ids", opts.nmIds.join(","));
    if (opts?.real) qs.set("real", "1");
    return apiFetch<PnlOpiy>(
      `/modules/sales/cabinets/${cabinetId}/pnl-opiy?${qs.toString()}`,
    );
  },
  getWbPnlOpiyMonth: (
    cabinetId: string,
    month: string,
    opts?: { subjects?: string[]; nmIds?: number[]; real?: boolean },
  ) => {
    const qs = new URLSearchParams({ month });
    if (opts?.subjects?.length) qs.set("subjects", opts.subjects.join(","));
    if (opts?.nmIds?.length) qs.set("nm_ids", opts.nmIds.join(","));
    if (opts?.real) qs.set("real", "1");
    return apiFetch<PnlOpiyMonth>(
      `/modules/sales/cabinets/${cabinetId}/pnl-opiy/month?${qs.toString()}`,
    );
  },
  getWbPnlOpiyRange: (
    cabinetId: string,
    start: string,
    end: string,
    opts?: { subjects?: string[]; nmIds?: number[]; granularity?: string; real?: boolean },
  ) => {
    const qs = new URLSearchParams({ start, end, granularity: opts?.granularity || "day" });
    if (opts?.subjects?.length) qs.set("subjects", opts.subjects.join(","));
    if (opts?.nmIds?.length) qs.set("nm_ids", opts.nmIds.join(","));
    if (opts?.real) qs.set("real", "1");
    return apiFetch<PnlOpiyRange>(
      `/modules/sales/cabinets/${cabinetId}/pnl-opiy/range?${qs.toString()}`,
    );
  },
  getWbPnlOpiyDaySkus: (
    cabinetId: string,
    date: string,
    opts?: { subjects?: string[]; nmIds?: number[]; real?: boolean },
  ) => {
    const qs = new URLSearchParams({ date });
    if (opts?.subjects?.length) qs.set("subjects", opts.subjects.join(","));
    if (opts?.nmIds?.length) qs.set("nm_ids", opts.nmIds.join(","));
    if (opts?.real) qs.set("real", "1");
    return apiFetch<PnlOpiyDaySkus>(
      `/modules/sales/cabinets/${cabinetId}/pnl-opiy/day-skus?${qs.toString()}`,
    );
  },
  startAdvArticleSync: (cabinetId: string, dateFrom: string, dateTo: string) =>
    apiFetch<{ status: string; message?: string }>(`/modules/sales/cabinets/${cabinetId}/sync/adv-article?date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}`, { method: "POST" }),
  getAdvArticleSyncStatus: (cabinetId: string) =>
    apiFetch<{ status: string; message?: string; synced?: number }>(`/modules/sales/cabinets/${cabinetId}/sync/adv-article/status`),

  // ── Заявки на доступ ────────────────────────────────────────────────────
  // Подача идёт С ЭКРАНА ВХОДА, где токена нет и быть не может: auth:false,
  // иначе apiFetch не поставит заголовок, но при 401 разошлёт «сессия
  // протухла» по порталу. Ответ сервера всегда одинаковый — форма по нему
  // ничего не узнаёт о том, есть такой сотрудник или нет.
  submitAccessRequest: (payload: import("./types").AccessRequestPayload) =>
    apiFetch<{ status: string }>("/access-requests", {
      method: "POST", auth: false, body: JSON.stringify(payload),
    }),
  listAccessRequests: (status?: string) =>
    apiFetch<import("./types").AccessRequest[]>(`/access-requests${status ? `?status=${status}` : ""}`),
  countAccessRequests: () =>
    apiFetch<{ new: number; open: number; names: string[] }>("/access-requests/count"),
  patchAccessRequest: (id: number, payload: { status?: string }) =>
    apiFetch<import("./types").AccessRequest>(`/access-requests/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteAccessRequest: (id: number) =>
    apiFetch<void>(`/access-requests/${id}`, { method: "DELETE" }),

  listDevRequests: (opts?: { status?: string; mine?: boolean }) => {
    const qs = new URLSearchParams();
    if (opts?.status) qs.set("status", opts.status);
    if (opts?.mine) qs.set("mine", "true");
    const q = qs.toString();
    return apiFetch<import("./types").DevRequest[]>(`/dev-requests${q ? `?${q}` : ""}`);
  },
  createDevRequest: (payload: import("./types").DevRequestPayload) =>
    apiFetch<import("./types").DevRequest>("/dev-requests", { method: "POST", body: JSON.stringify(payload) }),
  patchDevRequest: (id: number, payload: Partial<import("./types").DevRequestPayload> & { status?: string }) =>
    apiFetch<import("./types").DevRequest>(`/dev-requests/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteDevRequest: (id: number) =>
    apiFetch<void>(`/dev-requests/${id}`, { method: "DELETE" }),

  // ── Командная работа: задачи между сотрудниками + чат 1:1 ────────────────────
  listTasks: (box: "mine" | "inbox" | "outbox" | "all" = "mine", archived = false) =>
    apiFetch<import("./types").TeamTask[]>(`/team/tasks?box=${box}${archived ? "&archived=true" : ""}`),
  createTask: (payload: import("./types").TeamTaskPayload) =>
    apiFetch<import("./types").TeamTask>("/team/tasks", { method: "POST", body: JSON.stringify(payload) }),
  /** История задачи: кто, что и когда сделал (новые события первыми). */
  taskHistory: (id: number) =>
    apiFetch<import("./types").TaskEvent[]>(`/team/tasks/${id}/history`),
  patchTask: (id: number, payload: Partial<import("./types").TeamTaskPayload> & { status?: string; archived?: boolean }) =>
    apiFetch<import("./types").TeamTask>(`/team/tasks/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteTask: (id: number) =>
    apiFetch<void>(`/team/tasks/${id}`, { method: "DELETE" }),
  reorderTasks: (ids: number[]) =>
    apiFetch<{ ok: boolean }>("/team/tasks/reorder", { method: "POST", body: JSON.stringify({ ids }) }),
  // Этапы задачи (чек-лист: глобальный каталог этапов + значения/референсы задачи)
  // Этапы/ячейки задачи (getTaskCells, setTaskCell, getTaskStageCatalog) убраны
  // 02.09.2026 вместе с эндпоинтами: модуль «IT-проекты» удалён 28.07, запросы
  // отвечали 500. Новый формат задач этапов не имеет.
  // Личная «База знаний» (полезные ссылки)
  kbListLinks: () => apiFetch<import("./types").KbLink[]>("/kb/links"),
  kbCreateLink: (body: { category?: string; title?: string; url?: string; description?: string; media_url?: string; media_type?: string; media_caption?: string }) =>
    apiFetch<import("./types").KbLink>("/kb/links", { method: "POST", body: JSON.stringify(body) }),
  kbPatchLink: (id: number, body: Partial<import("./types").KbLink>) =>
    apiFetch<import("./types").KbLink>(`/kb/links/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  kbDeleteLink: (id: number) => apiFetch<void>(`/kb/links/${id}`, { method: "DELETE" }),
  // Документы Базы знаний (вики, личные/командные)
  kbListDocs: () => apiFetch<import("./types").KbDocMeta[]>("/kb/docs"),
  kbGetDoc: (id: number) => apiFetch<import("./types").KbDoc>(`/kb/docs/${id}`),
  kbCreateDoc: (body: import("./types").KbDocInput) =>
    apiFetch<import("./types").KbDoc>("/kb/docs", { method: "POST", body: JSON.stringify(body) }),
  kbPatchDoc: (id: number, body: import("./types").KbDocInput) =>
    apiFetch<import("./types").KbDoc>(`/kb/docs/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  kbDeleteDoc: (id: number) => apiFetch<void>(`/kb/docs/${id}`, { method: "DELETE" }),
  kbUploadFile: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch<{ url: string; filename: string; type?: "image" | "video" }>("/kb/files", { method: "POST", body: fd });
  },
  // Подзадачи задачи
  // ── Каналы внутреннего чата ─────────────────────────────────────────────
  /** Каналы, доступные мне, со счётчиком непрочитанного. */
  tmChannels: (archived = false) =>
    apiFetch<import("./types").TmChannel[]>(`/team/channels${archived ? "?archived=true" : ""}`),
  /** Убрать канал в архив или вернуть. Переписка остаётся — это не удаление. */
  tmChannelArchive: (id: number, on: boolean) =>
    apiFetch<{ ok: boolean; archived: boolean }>(`/team/channels/${id}/archive`,
      { method: "PUT", body: JSON.stringify({ on }) }),
  tmCreateChannel: (body: { name: string; title?: string; description?: string; is_private?: boolean }) =>
    apiFetch<{ id: number; name: string }>("/team/channels", { method: "POST", body: JSON.stringify(body) }),
  tmChannelMessages: (id: number) =>
    apiFetch<import("./types").ChatMessage[]>(`/team/channels/${id}/messages`),
  tmChannelSend: (id: number, body: { text: string; reply_to_id?: number | null }) =>
    apiFetch<import("./types").ChatMessage>(`/team/channels/${id}/messages`, { method: "POST", body: JSON.stringify(body) }),
  tmChannelJoin: (id: number, on: boolean) =>
    apiFetch<{ ok: boolean; member: boolean }>(`/team/channels/${id}/${on ? "join" : "leave"}`, { method: "POST" }),
  tmChannelPin: (id: number, msgId: number) =>
    apiFetch<{ ok: boolean; pinned_message_id: number | null }>(`/team/channels/${id}/pin/${msgId}`, { method: "PUT" }),
  tmChannelInfo: (id: number) =>
    apiFetch<import("./types").TmChannelInfo>(`/team/channels/${id}/info`),
  chatUsers: () => apiFetch<import("./types").ChatUser[]>("/team/users"),
  chatThread: (withId: string) =>
    apiFetch<import("./types").ChatMessage[]>(`/team/chat?with_id=${encodeURIComponent(withId)}`),
  chatSend: (recipientId: string, text: string, replyToId?: number | null) =>
    apiFetch<import("./types").ChatMessage>("/team/chat", { method: "POST", body: JSON.stringify({ recipient_id: recipientId, text, reply_to_id: replyToId ?? null }) }),
  editMessage: (id: number, text: string) =>
    apiFetch<import("./types").ChatMessage>(`/team/chat/${id}`, { method: "PATCH", body: JSON.stringify({ text }) }),
  deleteMessage: (id: number) =>
    apiFetch<void>(`/team/chat/${id}`, { method: "DELETE" }),
  reactMessage: (id: number, emoji: string) =>
    apiFetch<import("./types").ChatMessage>(`/team/chat/${id}/react`, { method: "POST", body: JSON.stringify({ emoji }) }),
  teamUnread: () => apiFetch<import("./types").TeamUnread>("/team/unread"),
  // вложения (файлы/медиа/ссылки)
  uploadTeamFile: async (parentType: "task" | "message" | "dev_request" | "task_cell", parentId: number, file: File) => {
    const fd = new FormData();
    fd.append("parent_type", parentType);
    fd.append("parent_id", String(parentId));
    fd.append("file", file);
    const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_STORAGE_KEY) : null;
    const res = await fetch(`${API_BASE}/team/attachments/file`, {
      method: "POST", headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd,
    });
    invalidateApiCache();
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
    return res.json() as Promise<import("./types").TeamAttachment>;
  },
  addTeamLink: (parentType: "task" | "message" | "dev_request" | "task_cell", parentId: number, url: string, title = "") =>
    apiFetch<import("./types").TeamAttachment>("/team/attachments/link", { method: "POST", body: JSON.stringify({ parent_type: parentType, parent_id: parentId, url, title }) }),
  patchTeamAttachment: (id: number, body: { title?: string; url?: string }) =>
    apiFetch<import("./types").TeamAttachment>(`/team/attachments/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteTeamAttachment: (id: number) =>
    apiFetch<void>(`/team/attachments/${id}`, { method: "DELETE" }),
  teamFileUrl: (url: string) => API_BASE.replace(/\/api$/, "") + url,

  // ── AI-чат (GPT через Codex-прокси) ──────────────────────────────────────────
  // Вкладка «Аналитик»: факты считает портал, текст даёт модель — по кнопке
  // «Пересобрать» и утренним расписанием (задача ceo_brief).
  /** Здоровье фоновых задач для колокольчика: что падает и что молчит. */
  jobsHealth: () => apiFetch<import("./types").JobsHealth>("/jobs/health"),
  ceoBrief: (scope = "all", month?: string) =>
    apiFetch<import("./types").BriefResponse>(
      `/ceo-brief?scope=${encodeURIComponent(scope)}${month ? `&month=${month}` : ""}`),
  ceoBriefRefresh: (scope = "all", month?: string) =>
    apiFetch<unknown>(
      `/ceo-brief/refresh?scope=${encodeURIComponent(scope)}${month ? `&month=${month}` : ""}`,
      { method: "POST" }),
  ceoBriefHistory: (scope = "all") =>
    apiFetch<{ id: number; date: string; status: string; headline: string; created_at: string }[]>(
      `/ceo-brief/history?scope=${encodeURIComponent(scope)}`),
  /** Сбывались ли прогнозы: прогноз раннего разбора месяца против его итога. */
  ceoBriefAccuracy: (scope = "all") =>
    apiFetch<import("./types").BriefAccuracyRow[]>(
      `/ceo-brief/accuracy?scope=${encodeURIComponent(scope)}`),
  /** Почему прибыль изменилась: разложение сдвига между месяцами по факторам. */
  ceoBriefBridge: (scope = "all", month?: string, base?: string) =>
    apiFetch<import("./types").BriefBridge>(
      `/ceo-brief/bridge?scope=${encodeURIComponent(scope)}` +
      (month ? `&month=${month}` : "") + (base ? `&base=${base}` : "")),
  /** Тот же разбор, но по одному артикулу: почему товар стал прибыльнее/убыточнее. */
  ceoBriefBridgeSku: (sku: string, scope = "all") =>
    apiFetch<import("./types").BriefBridge>(
      `/ceo-brief/bridge-sku?sa=${encodeURIComponent(sku)}&scope=${encodeURIComponent(scope)}`),
  ceoBriefOne: (id: number) =>
    apiFetch<import("./types").BriefSnapshot>(`/ceo-brief/${id}`),
  aiConversations: () => apiFetch<import("./types").AiConversation[]>("/ai/conversations"),
  aiConversation: (id: number) => apiFetch<import("./types").AiConversationDetail>(`/ai/conversations/${id}`),
  aiCreateConversation: (title = "Новый чат") =>
    apiFetch<import("./types").AiConversationDetail>("/ai/conversations", { method: "POST", body: JSON.stringify({ title }) }),
  aiRenameConversation: (id: number, title: string) =>
    apiFetch<import("./types").AiConversation>(`/ai/conversations/${id}`, { method: "PATCH", body: JSON.stringify({ title }) }),
  aiDeleteConversation: (id: number) =>
    apiFetch<void>(`/ai/conversations/${id}`, { method: "DELETE" }),
  aiSend: (id: number, text: string, attachmentUrl?: string | null, context?: string | null) =>
    apiFetch<import("./types").AiSendResult>(`/ai/conversations/${id}/messages`, { method: "POST", body: JSON.stringify({ text, attachment_url: attachmentUrl ?? null, context: context ?? null }) }),
  aiUploadFile: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch<{ url: string; type: "image" }>("/ai/files", { method: "POST", body: fd });
  },
  // Стриминг ответа (SSE): onEvent вызывается на каждое событие (reasoning/content/image/error/done).
  aiSendStream: async (
    id: number,
    text: string,
    onEvent: (e: import("./types").AiStreamEvent) => void,
    signal?: AbortSignal,
    context?: string | null,
  ): Promise<void> => {
    const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_STORAGE_KEY) : null;
    const res = await fetch(`${API_BASE}/ai/conversations/${id}/messages/stream`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ text, context: context ?? null }),
      signal,
    });
    if (!res.ok || !res.body) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let idx: number;
      while ((idx = buf.indexOf("\n\n")) >= 0) {
        const block = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        const line = block.split("\n").find((l) => l.startsWith("data:"));
        if (!line) continue;
        const d = line.slice(5).trim();
        if (!d) continue;
        try { onEvent(JSON.parse(d)); } catch { /* ignore partial */ }
      }
    }
  },

  // ── Requests module ─────────────────────────────────────────────────────────

  crGetSettings: () =>
    apiFetch<import("./types").CrSettings>("/modules/creator-reports/settings"),
  crSaveSettings: (payload: {
    bot_token?: string;
    apify_token?: string;
    vk_service_token?: string;
    platform_colors?: Record<string, string>;
    auto_sync_enabled?: boolean;
    auto_sync_weekdays?: number[];
    auto_sync_time?: string;
    auto_sync_max_age_days?: number;
  }) =>
    apiFetch<import("./types").CrSettings>("/modules/creator-reports/settings", { method: "PUT", body: JSON.stringify(payload) }),
  crListApifyTokens: () =>
    apiFetch<import("./types").ApifyTokenInfo[]>("/modules/creator-reports/settings/apify-tokens"),
  crAddApifyToken: (token: string) =>
    apiFetch<import("./types").ApifyTokenInfo>("/modules/creator-reports/settings/apify-tokens", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),
  crRemoveApifyToken: (id: string) =>
    apiFetch<void>(`/modules/creator-reports/settings/apify-tokens/${id}`, { method: "DELETE" }),
  crCleanup: (scope: "reports" | "mkt" | "plans" | "creators" | "all", confirm: string) =>
    apiFetch<{ ok: boolean; deleted: Record<string, number> }>("/modules/creator-reports/cleanup", {
      method: "POST",
      body: JSON.stringify({ scope, confirm }),
    }),

  crListCreators: (activeOnly = false) =>
    apiFetch<import("./types").Creator[]>(`/modules/creator-reports/creators${activeOnly ? "?active_only=true" : ""}`),
  crCreateCreator: (payload: { fio: string; tg_username?: string; tg_id?: string; hire_date?: string; fire_date?: string }) =>
    apiFetch<import("./types").Creator>("/modules/creator-reports/creators", { method: "POST", body: JSON.stringify(payload) }),
  crUpdateCreator: (id: string, payload: Partial<import("./types").Creator>) =>
    apiFetch<import("./types").Creator>(`/modules/creator-reports/creators/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  crDeleteCreator: (id: string) =>
    apiFetch<void>(`/modules/creator-reports/creators/${id}`, { method: "DELETE" }),

  // ── Блогеры/бартеры (справочники для интеграций) ──
  crListBloggers: (kind?: "blogger" | "barterer") =>
    apiFetch<import("./types").Blogger[]>(`/modules/creator-reports/bloggers${kind ? `?kind=${kind}` : ""}`),
  crCreateBlogger: (payload: Partial<import("./types").Blogger>) =>
    apiFetch<import("./types").Blogger>("/modules/creator-reports/bloggers", { method: "POST", body: JSON.stringify(payload) }),
  crUpdateBlogger: (id: string, payload: Partial<import("./types").Blogger>) =>
    apiFetch<import("./types").Blogger>(`/modules/creator-reports/bloggers/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  crDeleteBlogger: (id: string) =>
    apiFetch<void>(`/modules/creator-reports/bloggers/${id}`, { method: "DELETE" }),
  crRefreshBloggerStats: (id: string) =>
    apiFetch<import("./types").Blogger>(`/modules/creator-reports/bloggers/${id}/refresh-stats`, { method: "POST" }),
  crRefreshBloggersBulk: (kind?: "blogger" | "barterer") =>
    apiFetch<{ running: boolean; started?: boolean; total?: number; done?: number }>(`/modules/creator-reports/bloggers/refresh-stats-bulk${kind ? `?kind=${kind}` : ""}`, { method: "POST" }),
  crBloggersRefreshStatus: () =>
    apiFetch<{ running: boolean; total: number; done: number; kind: string | null; stopped: string }>("/modules/creator-reports/bloggers/refresh-status"),
  crUploadBloggerAvatar: async (id: string, file: File): Promise<import("./types").Blogger> => {
    const form = new FormData();
    form.append("file", file);
    const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_STORAGE_KEY) : null;
    const res = await fetch(`${API_BASE}/modules/creator-reports/bloggers/${id}/avatar`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json() as Promise<import("./types").Blogger>;
  },
  crDeleteBloggerAvatar: (id: string) =>
    apiFetch<import("./types").Blogger>(`/modules/creator-reports/bloggers/${id}/avatar`, { method: "DELETE" }),
  crImportBloggersXlsx: async (file: File, kind?: "blogger" | "barterer"): Promise<{ added: number; duplicates: number; skipped: number; total: number }> => {
    const form = new FormData();
    form.append("file", file);
    const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_STORAGE_KEY) : null;
    const res = await fetch(`${API_BASE}/modules/creator-reports/bloggers/import-xlsx${kind ? `?kind=${kind}` : ""}`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  // ── Интеграции ──
  crListIntegrations: () =>
    apiFetch<import("./types").Integration[]>("/modules/creator-reports/integrations"),
  crCreateIntegration: (payload: Partial<import("./types").Integration>) =>
    apiFetch<import("./types").Integration>("/modules/creator-reports/integrations", { method: "POST", body: JSON.stringify(payload) }),
  crUpdateIntegration: (id: string, payload: Partial<import("./types").Integration>) =>
    apiFetch<import("./types").Integration>(`/modules/creator-reports/integrations/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  crDeleteIntegration: (id: string) =>
    apiFetch<void>(`/modules/creator-reports/integrations/${id}`, { method: "DELETE" }),
  // Подтянуть метрики поста (просмотры/лайки/комменты/сохранения) из Apify по content_url.
  crRefreshIntegrationMetrics: (id: string) =>
    apiFetch<import("./types").Integration>(`/modules/creator-reports/integrations/${id}/refresh-metrics`, { method: "POST" }),

  crListReports: (params?: import("./types").CrReportFilters) => {
    const q = new URLSearchParams();
    if (params?.creator_id) q.set("creator_id", params.creator_id);
    if (params?.platform) q.set("platform", params.platform);
    if (params?.date_from) q.set("date_from", params.date_from);
    if (params?.date_to) q.set("date_to", params.date_to);
    if (params?.limit) q.set("limit", String(params.limit));
    if (params?.offset) q.set("offset", String(params.offset));
    return apiFetch<{ items: import("./types").CreatorReport[]; total: number }>(`/modules/creator-reports/reports?${q}`);
  },
  crUpdateReport: (id: string, payload: Partial<import("./types").CreatorReport>) =>
    apiFetch<import("./types").CreatorReport>(`/modules/creator-reports/reports/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  crDeleteReport: (id: string) =>
    apiFetch<void>(`/modules/creator-reports/reports/${id}`, { method: "DELETE" }),
  crRefreshMetrics: (id: string) =>
    apiFetch<import("./types").CreatorReport>(`/modules/creator-reports/reports/${id}/refresh-metrics`, { method: "POST" }),
  crGetRefreshStatus: () =>
    apiFetch<{ queued: number; active: number; pending: number }>(
      "/modules/creator-reports/reports/refresh-status",
    ),
  crCancelRefresh: () =>
    apiFetch<{ dropped: number; in_flight: number; message: string }>(
      "/modules/creator-reports/reports/cancel-refresh",
      { method: "POST" },
    ),
  crBulkRefreshMetrics: (
    mode: "pending" | "all" | "recent" = "pending",
    maxAgeDays?: number,
    filters?: import("./types").CrReportFilters,
  ) => {
    const q = new URLSearchParams({ mode });
    if (maxAgeDays != null) q.set("max_age_days", String(maxAgeDays));
    if (filters?.date_from) q.set("date_from", filters.date_from);
    if (filters?.date_to) q.set("date_to", filters.date_to);
    if (filters?.creator_id) q.set("creator_id", filters.creator_id);
    if (filters?.platform) q.set("platform", filters.platform);
    return apiFetch<{ queued: number; message: string }>(
      `/modules/creator-reports/reports/bulk-refresh-metrics?${q.toString()}`,
      { method: "POST" },
    );
  },

  crImportXlsx: async (file: File) => {
    const form = new FormData();
    form.append("file", file);
    const resp = await fetch(`${API_BASE}/modules/creator-reports/import-xlsx`, {
      method: "POST",
      headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_STORAGE_KEY) ?? ""}` },
      body: form,
    });
    if (!resp.ok) throw new Error(await resp.text());
    invalidateApiCache();
    return resp.json() as Promise<{ created: number; skipped: number; errors: number }>;
  },

  crExportXlsxUrl: (params?: import("./types").CrReportFilters) => {
    const q = new URLSearchParams();
    if (params?.creator_id) q.set("creator_id", params.creator_id);
    if (params?.platform) q.set("platform", params.platform);
    if (params?.date_from) q.set("date_from", params.date_from);
    if (params?.date_to) q.set("date_to", params.date_to);
    return `${API_BASE}/modules/creator-reports/export-xlsx?${q}`;
  },

  crGetStats: (params?: { date_from?: string; date_to?: string; creator_types?: string[] }) => {
    const parts: string[] = [];
    if (params?.date_from) parts.push(`date_from=${encodeURIComponent(params.date_from)}`);
    if (params?.date_to) parts.push(`date_to=${encodeURIComponent(params.date_to)}`);
    if (params?.creator_types?.length) params.creator_types.forEach(t => parts.push(`creator_types=${encodeURIComponent(t)}`));
    const qs = parts.join("&");
    return apiFetch<import("./types").CrStats>(`/modules/creator-reports/stats${qs ? `?${qs}` : ""}`);
  },

  mktList: (channel: string, params?: { date_from?: string; date_to?: string }) => {
    const q = new URLSearchParams({ channel, ...(params ?? {}) });
    return apiFetch<{ items: import("./types").MktReport[]; total: number }>(
      `/modules/creator-reports/mkt?${q}`
    );
  },
  mktCreate: (body: Omit<import("./types").MktReport, "id" | "created_at" | "updated_at">) =>
    apiFetch<import("./types").MktReport>("/modules/creator-reports/mkt", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  mktUpdate: (id: string, body: Partial<import("./types").MktReport>) =>
    apiFetch<import("./types").MktReport>(`/modules/creator-reports/mkt/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  mktDelete: (id: string) =>
    apiFetch<void>(`/modules/creator-reports/mkt/${id}`, { method: "DELETE" }),
  mktImportXlsx: (file: File) => {
    const fd = new FormData(); fd.append("file", file);
    return apiFetch<{ created: number; skipped: number; errors: number }>(
      "/modules/creator-reports/mkt/import-xlsx", { method: "POST", body: fd }
    );
  },
  mktRefreshMetrics: (channel: string) =>
    apiFetch<{ queued: number; message: string }>(
      `/modules/creator-reports/mkt/refresh-metrics?channel=${channel}`,
      { method: "POST" }
    ),
  // Обновить метрики только по показанным записям (учёт фильтров таблицы).
  mktRefreshMetricsByIds: (ids: string[]) =>
    apiFetch<{ queued: number; message: string }>(
      `/modules/creator-reports/mkt/refresh-metrics`,
      { method: "POST", body: JSON.stringify({ ids }) }
    ),

  // ── ANS: подменные артикулы WB ──────────────────────────────────────────
  ansList: (params?: { source?: string; search?: string; recipient?: string }) => {
    const q = new URLSearchParams();
    if (params?.source) q.set("source", params.source);
    if (params?.search) q.set("search", params.search);
    if (params?.recipient) q.set("recipient", params.recipient);
    const qs = q.toString();
    return apiFetch<{ items: import("./types").AnsReport[]; total: number }>(
      `/modules/creator-reports/ans${qs ? `?${qs}` : ""}`
    );
  },
  ansImportXlsx: (file: File, recipient?: string) => {
    const fd = new FormData(); fd.append("file", file);
    return apiFetch<{ created: number; updated: number; skipped: number; errors: number }>(
      `/modules/creator-reports/ans/import-xlsx${recipient ? `?recipient=${recipient}` : ""}`, { method: "POST", body: fd }
    );
  },
  ansDelete: (id: string) =>
    apiFetch<void>(`/modules/creator-reports/ans/${id}`, { method: "DELETE" }),

  crGetCreatorRanking: (month: string, platform = "") =>
    apiFetch<import("./types").CrCreatorRanking[]>(
      `/modules/creator-reports/creator-ranking?month=${month}&platform=${platform}`
    ),

  // Рейтинг за произвольный период (даты YYYY-MM-DD, включительно).
  crGetCreatorRankingRange: (dateFrom: string, dateTo: string, platform = "") =>
    apiFetch<import("./types").CrCreatorRanking[]>(
      `/modules/creator-reports/creator-ranking?date_from=${dateFrom}&date_to=${dateTo}&platform=${platform}`
    ),

  // Выплаты креаторам (ЗП за месяц: авто-расчёт + ручные правки).
  crGetPayouts: (month: string) =>
    apiFetch<{ items: import("./types").CrPayoutRow[]; categories: Record<string, import("./types").CrPayoutCategory> }>(
      `/modules/creator-reports/payouts?month=${month}`),
  crSetPayout: (creatorId: string, month: string, patch: import("./types").CrPayoutPatch) =>
    apiFetch<import("./types").CrPayoutRow>(`/modules/creator-reports/payouts/${creatorId}?month=${month}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),

  // План просмотров на месяц per-креатор ({creator_id: plan_views}).
  crGetCreatorPlans: (month: string) =>
    apiFetch<Record<string, number>>(`/modules/creator-reports/creator-plans?month=${month}`),
  crSetCreatorPlan: (creatorId: string, month: string, planViews: number) =>
    apiFetch<{ ok: boolean }>(`/modules/creator-reports/creator-plans/${creatorId}?month=${month}`, {
      method: "PUT",
      body: JSON.stringify({ plan_views: planViews }),
    }),

  crGetArticles: (month: string) =>
    apiFetch<import("./types").CrArticleStat[]>(`/modules/creator-reports/articles?month=${month}`),

  crUpsertArticlePlan: (article: string, month: string, plan_posts: number, plan_views = 0) =>
    apiFetch<{ ok: boolean }>(`/modules/creator-reports/articles/${encodeURIComponent(article)}/plan?month=${month}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ plan_posts, plan_views }),
    }),

  crDeleteArticlePlan: (article: string, month: string) =>
    apiFetch<{ ok: boolean }>(`/modules/creator-reports/articles/${encodeURIComponent(article)}/plan?month=${month}`, {
      method: "DELETE",
    }),

  // ── планирование: настройки кабинета и статьи расходов ──────────────────
  rdListSeasons: () =>
    apiFetch<import("./rd-collection").RdSeason[]>("/rd-collection/seasons"),
  rdCreateSeason: (payload: { code: string; title: string; status?: "active" | "archived"; sort_order?: number }) =>
    apiFetch<import("./rd-collection").RdSeason>("/rd-collection/seasons", {
      method: "POST",
      body: JSON.stringify({ status: "active", sort_order: 0, ...payload }),
    }),
  rdListBrands: () =>
    apiFetch<import("./rd-collection").RdBrand[]>("/rd-collection/brands"),
  rdListTransportCompanies: () =>
    apiFetch<import("./rd-collection").RdTransportCompany[]>("/rd-collection/transport-companies"),
  rdCreateTransportCompany: (payload: Omit<import("./rd-collection").RdTransportCompany, "id">) =>
    apiFetch<import("./rd-collection").RdTransportCompany>("/rd-collection/transport-companies", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  rdUpdateTransportCompany: (id: number, payload: Omit<import("./rd-collection").RdTransportCompany, "id">) =>
    apiFetch<import("./rd-collection").RdTransportCompany>(`/rd-collection/transport-companies/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  rdDeleteTransportCompany: (id: number) =>
    apiFetch<void>(`/rd-collection/transport-companies/${id}`, { method: "DELETE" }),

  rdListLegalEntities: () =>
    apiFetch<import("./rd-collection").RdLegalEntity[]>("/rd-collection/legal-entities"),
  rdListContractors: () =>
    apiFetch<import("./rd-collection").RdContractor[]>("/rd-collection/contractors"),
  rdCreateContractor: (payload: Omit<import("./rd-collection").RdContractor, "id">) =>
    apiFetch<import("./rd-collection").RdContractor>("/rd-collection/contractors", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  rdUpdateContractor: (id: number, payload: Omit<import("./rd-collection").RdContractor, "id">) =>
    apiFetch<import("./rd-collection").RdContractor>(`/rd-collection/contractors/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  rdDeleteContractor: (id: number) =>
    apiFetch<void>(`/rd-collection/contractors/${id}`, { method: "DELETE" }),

  // Логотип записи справочника (kind: brands | legal-entities | contractors | logistics-countries)
  rdUploadLogo: (kind: string, id: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch<{ id: number; logo_url: string }>(`/rd-collection/${kind}/${id}/logo`, { method: "POST", body: fd });
  },
  rdDeleteLogo: (kind: string, id: number) =>
    apiFetch<{ id: number; logo_url: string }>(`/rd-collection/${kind}/${id}/logo`, { method: "DELETE" }),

  // ── Вкладка «Себестоимость» (все артикулы из приёмок сезона) ──
  rdCostPrice: (seasonId: number) =>
    apiFetch<import("./rd-collection").CostPriceReport>(`/rd-collection/cost-price?season_id=${seasonId}`),
  // Excel-выгрузка: без seasonId — все сезоны всех годов.
  rdCostPriceXlsx: (seasonId?: number) =>
    apiBlobFetch(`/rd-collection/cost-price/export.xlsx${seasonId ? `?season_id=${seasonId}` : ""}`),

  // ── Справочник «Логистика» (расценки/кг + сроки этапов по стране×виду) ──
  rdLogisticsMatrix: () =>
    apiFetch<import("./rd-collection").RdLogisticsMatrix>("/rd-collection/logistics-matrix"),
  rdCreateLogisticsCountry: (name: string) =>
    apiFetch<import("./rd-collection").RdLogisticsCountry>("/rd-collection/logistics-countries", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),
  rdDeleteLogisticsCountry: (id: number) =>
    apiFetch<void>(`/rd-collection/logistics-countries/${id}`, { method: "DELETE" }),
  rdUpsertLogisticsRate: (country_id: number, transport_mode: string, rate_per_kg: number | null) =>
    apiFetch<import("./rd-collection").RdLogisticsRate>("/rd-collection/logistics-rate", {
      method: "PUT",
      body: JSON.stringify({ country_id, transport_mode, rate_per_kg }),
    }),
  rdUpsertLogisticsStageNorm: (country_id: number, transport_mode: string, column_id: number, days: number | null) =>
    apiFetch<import("./rd-collection").RdLogisticsStageNorm>("/rd-collection/logistics-stage-norm", {
      method: "PUT",
      body: JSON.stringify({ country_id, transport_mode, column_id, days }),
    }),

  rdListProducts: (seasonId: number, activeOnly = true) =>
    apiFetch<import("./rd-collection").RdProduct[]>(
      `/rd-collection/products?season_id=${seasonId}&active_only=${activeOnly}`,
    ),
  rdListDraftProducts: (activeOnly = true) =>
    apiFetch<import("./rd-collection").RdProduct[]>(
      `/rd-collection/products?drafts=true&active_only=${activeOnly}`,
    ),
  rdImportSku: (seasonId: number, rows: import("./rd-collection").RdSkuImportRow[], dryRun = false) =>
    apiFetch<import("./rd-collection").RdSkuImportResult>("/rd-collection/products/import", {
      method: "POST",
      body: JSON.stringify({ season_id: seasonId, rows, dry_run: dryRun }),
    }),

  rdSeasonDashboard: (seasonId: number, activeOnly = true) =>
    apiFetch<import("./rd-collection").RdSeasonDashboard>(
      `/rd-collection/seasons/${seasonId}/dashboard?active_only=${activeOnly}`,
    ),

  rdListOrders: (seasonId: number | null, opts?: { status?: string; year?: number | null }) => {
    const q = new URLSearchParams();
    if (seasonId != null) q.set("season_id", String(seasonId));
    if (opts?.status) q.set("status", opts.status);
    if (opts?.year != null) q.set("year", String(opts.year));
    return apiFetch<import("./rd-collection").RdOrderListItem[]>(`/rd-collection/orders?${q.toString()}`);
  },
  rdGetOrder: (id: number) =>
    apiFetch<import("./rd-collection").RdOrder>(`/rd-collection/orders/${id}`),
  rdCreateOrder: (
    payload: Omit<
      import("./rd-collection").RdOrder,
      "id" | "created_at" | "updated_at" | "lines" | "shipments" | "qty_planned_total" | "qty_ordered_total" | "qty_shipped_total" | "order_value_currency" | "order_value_rub"
    > & { lines: { product_id: number; qty_planned: number; qty_ordered: number; notes?: string }[] },
  ) =>
    apiFetch<import("./rd-collection").RdOrder>("/rd-collection/orders", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  rdUpdateOrder: (
    id: number,
    payload: Omit<
      import("./rd-collection").RdOrder,
      "id" | "created_at" | "updated_at" | "lines" | "shipments" | "qty_planned_total" | "qty_ordered_total" | "qty_shipped_total" | "order_value_currency" | "order_value_rub"
    > & { lines: { product_id: number; qty_planned: number; qty_ordered: number; notes?: string }[] },
  ) =>
    apiFetch<import("./rd-collection").RdOrder>(`/rd-collection/orders/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  rdCreateShipment: (
    orderId: number,
    payload: Omit<
      import("./rd-collection").RdShipment,
      "id" | "order_id" | "created_at" | "updated_at" | "lines" | "receipt_lines" | "total_qty_shipped" | "total_qty_received" | "cost_total_rub" | "cost_overhead_pct" | "freight_overhead_pct" | "total_to_pay_invoice" | "op_cycle_days"
    > & { lines: { product_id: number; qty_shipped: number; price_unit?: number }[] },
  ) =>
    apiFetch<import("./rd-collection").RdShipment>(`/rd-collection/orders/${orderId}/shipments`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  rdUpdateShipment: (
    id: number,
    payload: Omit<
      import("./rd-collection").RdShipment,
      "id" | "order_id" | "created_at" | "updated_at" | "lines" | "receipt_lines" | "total_qty_shipped" | "total_qty_received" | "cost_total_rub" | "cost_overhead_pct" | "freight_overhead_pct" | "total_to_pay_invoice" | "op_cycle_days"
    > & { lines: { product_id: number; qty_shipped: number; price_unit?: number }[] },
  ) =>
    apiFetch<import("./rd-collection").RdShipment>(`/rd-collection/shipments/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  rdDeleteShipment: (id: number) =>
    apiFetch<void>(`/rd-collection/shipments/${id}`, { method: "DELETE" }),
  rdRefreshCbrRate: (id: number, onDate?: string) => {
    const q = onDate ? `?on_date=${onDate}` : "";
    return apiFetch<import("./rd-collection").RdShipment>(`/rd-collection/shipments/${id}/cbr-rate${q}`, { method: "POST" });
  },

  // ── R&D Collection · логистика рейсов ──────────────────────────────────
  rdListLogistics: (seasonId?: number, year?: number | null, includeClosed?: boolean) => {
    const q = new URLSearchParams();
    if (seasonId) q.set("season_id", String(seasonId));
    if (year != null) q.set("year", String(year));
    if (includeClosed) q.set("include_closed", "true");
    const qs = q.toString();
    return apiFetch<import("./rd-collection").RdShipmentLogistics[]>(
      `/rd-collection/logistics${qs ? "?" + qs : ""}`,
    );
  },
  rdCreateLogistics: (payload: import("./rd-collection").RdShipmentLogisticsIn) =>
    apiFetch<import("./rd-collection").RdShipmentLogistics>("/rd-collection/logistics", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  rdUpdateLogistics: (
    id: number,
    payload: import("./rd-collection").RdShipmentLogisticsIn,
  ) =>
    apiFetch<import("./rd-collection").RdShipmentLogistics>(`/rd-collection/logistics/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  rdLogisticsStatusHistory: (id: number) =>
    apiFetch<import("./rd-collection").RdLogisticsStatusHistoryEntry[]>(
      `/rd-collection/logistics/${id}/status-history`,
    ),
  rdGetLogisticsComposition: (id: number) =>
    apiFetch<import("./rd-collection").RdLogisticsComposition>(
      `/rd-collection/logistics/${id}/composition`,
    ),
  rdLogisticsStagesProgress: (id: number) =>
    apiFetch<import("./rd-collection").RdLogisticsStageProgress[]>(
      `/rd-collection/logistics/${id}/stages-progress`,
    ),
  rdLogisticsShipmentsDetail: (id: number) =>
    apiFetch<import("./rd-collection").RdLogisticsShipmentItem[]>(
      `/rd-collection/logistics/${id}/shipments-detail`,
    ),

  // ── R&D Collection · упаковочные листы рейса (packing list) ─────────────
  rdListPackingLists: (logisticsId: number) =>
    apiFetch<import("./rd-collection").RdPackingListSummary[]>(
      `/rd-collection/logistics/${logisticsId}/packing-lists`,
    ),
  rdCreatePackingList: (
    logisticsId: number,
    payload: import("./rd-collection").RdPackingListIn,
  ) =>
    apiFetch<import("./rd-collection").RdPackingList>(
      `/rd-collection/logistics/${logisticsId}/packing-lists`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
  rdGetPackingList: (packingListId: number) =>
    apiFetch<import("./rd-collection").RdPackingList>(
      `/rd-collection/packing-lists/${packingListId}`,
    ),
  rdDeletePackingList: (packingListId: number) =>
    apiFetch<void>(`/rd-collection/packing-lists/${packingListId}`, { method: "DELETE" }),
  rdPackingTemplateBlob: (packingListId: number) =>
    apiBlobFetch(`/rd-collection/packing-lists/${packingListId}/template.xlsx`),
  rdPackingChecklistBlob: (packingListId: number) =>
    apiBlobFetch(`/rd-collection/packing-lists/${packingListId}/checklist.xlsx`),
  rdPackingLabelsBlob: (packingListId: number) =>
    apiBlobFetch(`/rd-collection/packing-lists/${packingListId}/labels.pdf`),
  rdImportPacking: (packingListId: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch<import("./rd-collection").RdPackingImportResult>(
      `/rd-collection/packing-lists/${packingListId}/import`,
      { method: "POST", body: fd },
    );
  },
  rdPackingScan: (code: string) =>
    apiFetch<import("./rd-collection").RdPackingScanResult>(
      `/rd-collection/packing/scan/${encodeURIComponent(code)}`,
    ),
  rdSetPackingBoxChecked: (boxId: number, checked: boolean) =>
    apiFetch<import("./rd-collection").RdPackingBox>(
      `/rd-collection/packing-boxes/${boxId}/check`,
      { method: "POST", body: JSON.stringify({ checked }) },
    ),

  // ── R&D Collection · колонки канбана логистики (общие для команды) ──────
  rdListKanbanColumns: () =>
    apiFetch<import("./rd-collection").RdWaybillKanbanColumn[]>(
      "/rd-collection/kanban-columns",
    ),
  rdCreateKanbanColumn: (payload: import("./rd-collection").RdWaybillKanbanColumnIn) =>
    apiFetch<import("./rd-collection").RdWaybillKanbanColumn>("/rd-collection/kanban-columns", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  rdUpdateKanbanColumn: (
    id: number,
    payload: import("./rd-collection").RdWaybillKanbanColumnPatch,
  ) =>
    apiFetch<import("./rd-collection").RdWaybillKanbanColumn>(`/rd-collection/kanban-columns/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  rdDeleteKanbanColumn: (id: number) =>
    apiFetch<void>(`/rd-collection/kanban-columns/${id}`, { method: "DELETE" }),

  // ── R&D Collection · МойСклад sync ──────────────────────────────────────
  rdMskladState: () =>
    apiFetch<import("./rd-collection").MskladSyncState>("/rd-collection/msklad/state"),
  rdMskladRunSync: (fullResync = false) =>
    apiFetch<import("./rd-collection").MskladSyncResult>(
      `/rd-collection/msklad/sync?full_resync=${fullResync}`,
      { method: "POST" },
    ),
  rdMskladListReceipts: (onlyUnattached = false, limit = 200, year?: number | null) => {
    const q = new URLSearchParams({ only_unattached: String(onlyUnattached), limit: String(limit) });
    if (year != null) q.set("year", String(year));
    return apiFetch<import("./rd-collection").MskladReceiptListItem[]>(
      `/rd-collection/msklad/receipts?${q.toString()}`,
    );
  },
  rdMskladGetReceipt: (id: number) =>
    apiFetch<import("./rd-collection").MskladReceipt>(`/rd-collection/msklad/receipts/${id}`),
  rdDefectsBySupplier: (year?: number | null) => {
    const q = new URLSearchParams();
    if (year != null) q.set("year", String(year));
    const qs = q.toString();
    return apiFetch<import("./rd-collection").DefectsBySupplier[]>(
      `/rd-collection/msklad/defects-by-supplier${qs ? `?${qs}` : ""}`,
    );
  },
  rdMskladUpdateReceiptLine: (receiptId: number, lineId: number, defectsQty: number) =>
    apiFetch<import("./rd-collection").MskladReceipt>(
      `/rd-collection/msklad/receipts/${receiptId}/lines/${lineId}`,
      { method: "PATCH", body: JSON.stringify({ defects_qty: defectsQty }) },
    ),
  rdLogisticsSummary: () =>
    apiFetch<import("./rd-collection").LogisticsSummary>(
      "/rd-collection/logistics/summary",
    ),
  rdMskladImportDefects: (file: File, year?: number | null) => {
    const fd = new FormData();
    fd.append("file", file);
    const qs = year != null ? `?year=${year}` : "";
    return apiFetch<import("./rd-collection").DefectsImportResult>(
      `/rd-collection/msklad/defects-import${qs}`,
      { method: "POST", body: fd },
    );
  },
  listIntegrations: () =>
    apiFetch<import("./integrations").PortalIntegration[]>("/integrations"),
  revealIntegration: (key: string) =>
    apiFetch<import("./integrations").PortalIntegrationRevealed>(`/integrations/${encodeURIComponent(key)}/reveal`),
  updateIntegration: (key: string, value: string, description = "") =>
    apiFetch<import("./integrations").PortalIntegration>(`/integrations/${encodeURIComponent(key)}`, {
      method: "PUT",
      body: JSON.stringify({ value, description }),
    }),
  deleteIntegration: (key: string) =>
    apiFetch<void>(`/integrations/${encodeURIComponent(key)}`, { method: "DELETE" }),
  /** Реестр ВСЕХ секретов портала: где лежат, настроены ли, когда истекают.
   *  Значений не отдаёт — только маски и метаданные. */
  // ── Справочник владения: клиент → ИП → кабинет → бренд ────────────────────
  // ── Фоновые задачи (синхронизации) ────────────────────────────────────────
  getPortalJobs: () => apiFetch<import("./jobs").PortalJobsResponse>("/jobs"),
  updatePortalJob: (key: string, patch: import("./jobs").PortalJobPatch) =>
    apiFetch<import("./jobs").PortalJob>(`/jobs/${encodeURIComponent(key)}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),

  tenancyTree: () => apiFetch<import("./tenancy").TenancyTree>("/tenancy/tree"),
  /** Что можно связать автоматически. apply=false — только показать план. */
  tenancyAutolink: (apply: boolean) =>
    apiFetch<{ applied: boolean; changes: import("./tenancy").TenancyAutolinkChange[] }>(
      `/tenancy/autolink?apply=${apply}`, { method: "POST" }),
  /** Код клиента портал придумывает сам из названия. */
  tenancyCreateClient: (body: { title: string; note?: string }) =>
    apiFetch<{ id: number; code: string; title: string }>("/tenancy/clients", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  tenancyCreateOrg: (body: { title: string; client_id: number | null; brand_title?: string }) =>
    apiFetch<{ id: number; title: string; brand: string }>("/tenancy/orgs", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Переименовать ИП или сменить бренд. Название поедет во все модули. */
  tenancyPatchOrg: (id: number, body: { title?: string; brand_title?: string }) =>
    apiFetch<{ id: number; title: string }>(`/tenancy/orgs/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  tenancyCreateBrand: (title: string) =>
    apiFetch<{ id: number; title: string }>("/tenancy/brands", {
      method: "POST",
      body: JSON.stringify({ title }),
    }),
  tenancyPatchBrand: (id: number, title: string) =>
    apiFetch<{ id: number; title: string }>(`/tenancy/brands/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ title }),
    }),
  tenancyDeleteBrand: (id: number) =>
    apiFetch<{ ok: boolean }>(`/tenancy/brands/${id}`, { method: "DELETE" }),
  tenancyDeleteClient: (id: number) =>
    apiFetch<{ ok: boolean }>(`/tenancy/clients/${id}`, { method: "DELETE" }),
  /** Переставить ИП другому клиенту (null — оставить без клиента). */
  tenancySetOrgClient: (orgId: number, clientId: number | null) =>
    apiFetch<{ id: number }>(`/tenancy/orgs/${orgId}/client`, {
      method: "POST",
      body: JSON.stringify({ client_id: clientId }),
    }),
  /** Завести кабинет площадки: создаётся в нужном модуле и сразу привязывается
   *  к ИП, токен уходит в шифрованное хранилище. */
  tenancyCreateCabinet: (body: { module: string; legal_entity_id: number; token: string }) =>
    apiFetch<{ module: string; cabinet_id: string; title: string; secured: boolean }>("/tenancy/cabinets", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Привязать кабинет модуля к ИП (null — снять привязку). */
  tenancyLinkCabinet: (module: string, cabinetId: string, legalEntityId: number | null) =>
    apiFetch<{ ok: boolean }>("/tenancy/cabinets/link", {
      method: "POST",
      body: JSON.stringify({ module, cabinet_id: cabinetId, legal_entity_id: legalEntityId }),
    }),
  inspectSecretValue: (value: string) =>
    apiFetch<import("./integrations").SecretInspect>("/integrations/registry/inspect", {
      method: "POST",
      body: JSON.stringify({ value }),
    }),
  /** Где ещё записан этот же токен. */
  secretCopies: (entryId: string) =>
    apiFetch<import("./integrations").SecretCopy[]>(
      `/integrations/registry/${encodeURIComponent(entryId)}/copies`),
  /** Записать токен в его родное хранилище (и, по желанию, во все копии). */
  writeSecret: (entryId: string, value: string, targets: string[]) =>
    apiFetch<import("./integrations").SecretWriteResult>(
      `/integrations/registry/${encodeURIComponent(entryId)}`, {
        method: "PUT",
        // targets — ровно те записи, которые пользователь видел и отметил.
        body: JSON.stringify({ value, targets }),
      }),

  rdMskladAttachReceipt: (
    receiptId: number,
    payload: { order_id: number; invoice_no?: string | null; ship_date?: string | null; plan_arrival_date?: string | null; skip_unmatched?: boolean },
  ) =>
    apiFetch<import("./rd-collection").MskladReceipt>(
      `/rd-collection/msklad/receipts/${receiptId}/attach`,
      { method: "POST", body: JSON.stringify({ skip_unmatched: true, ...payload }) },
    ),

  // ── R&D Collection · R3: Pivot + Решения ────────────────────────────────
  rdSeasonPivot: (seasonId: number, year?: number | null) => {
    const q = year != null ? `?year=${year}` : "";
    return apiFetch<import("./rd-collection").SeasonPivot>(`/rd-collection/seasons/${seasonId}/pivot${q}`);
  },
  rdSeasonInProgress: (seasonId: number, year?: number | null) => {
    const q = year != null ? `?year=${year}` : "";
    return apiFetch<import("./rd-collection").RdInProgressList>(`/rd-collection/seasons/${seasonId}/in-progress${q}`);
  },
  rdIncomingGoods: (historyDays = 45) =>
    apiFetch<import("./rd-collection").IncomingGoodsOut>(`/rd-collection/incoming-goods?history_days=${historyDays}`),
  rdSeasonAssortment: (seasonId?: number | null) =>
    apiFetch<import("./rd-collection").SeasonAssortmentOut>(`/rd-collection/season-assortment${seasonId ? `?season_id=${seasonId}` : ""}`),
  rdFfStockStatus: () =>
    apiFetch<import("./rd-collection").FfStockStatus>(`/rd-collection/ff-stock/status`),
  rdUploadFfStock: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch<import("./rd-collection").FfStockUploadResult>(
      `/rd-collection/ff-stock/upload`, { method: "POST", body: fd });
  },
  rdPatchCardFields: (productId: number, fields: import("./rd-collection").ProductCardFields) =>
    apiFetch(`/rd-collection/products/${productId}/card-fields`, { method: "PATCH", body: JSON.stringify(fields) }),
  rdAssortmentPick: () =>
    apiFetch<import("./rd-collection").AssortmentPickOut>(`/rd-collection/assortment-pick`),
  rdAssortmentSetSeasons: (modelCode: string, color: string, seasonIds: number[]) =>
    apiFetch(`/rd-collection/assortment-pick/set-seasons`, { method: "POST", body: JSON.stringify({ model_code: modelCode, color, season_ids: seasonIds }) }),
  rdAssortmentSetLifecycle: (
    articles: { model_code: string; color: string }[],
    status: import("./rd-collection").LifecycleStatus,
  ) =>
    apiFetch<{ updated: number; status: string }>(`/rd-collection/assortment-pick/set-lifecycle`, {
      method: "POST", body: JSON.stringify({ articles, status }),
    }),
  /** Заполнить паспорт товара (декларация, упаковка) из карточек Wildberries.
   *  dry_run=true — только посчитать, ничего не записывая (предпросмотр). */
  assortmentFillFromWb: (body: {
    overwrite?: boolean;
    fields?: import("./rd-collection").AssortmentWbField[];
    dry_run?: boolean;
  }) =>
    apiFetch<import("./rd-collection").AssortmentFillFromWbResult>(
      `/rd-collection/assortment-pick/fill-from-wb`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  // ── Приложения (встроенные ссылки на Figma/Miro/Google) ──────────────────
  listAppEmbeds: (deptKey?: string) =>
    apiFetch<import("./app-embeds").AppEmbed[]>(
      `/app-embeds${deptKey ? `?dept_key=${encodeURIComponent(deptKey)}` : ""}`),
  createAppEmbed: (body: import("./app-embeds").AppEmbedIn) =>
    apiFetch<import("./app-embeds").AppEmbed>(`/app-embeds`, { method: "POST", body: JSON.stringify(body) }),
  patchAppEmbed: (id: number, body: Partial<import("./app-embeds").AppEmbedIn> & { sort_order?: number }) =>
    apiFetch<import("./app-embeds").AppEmbed>(`/app-embeds/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteAppEmbed: (id: number) =>
    apiFetch<{ deleted: number }>(`/app-embeds/${id}`, { method: "DELETE" }),
  rdListDecisions: (productId: number) =>
    apiFetch<import("./rd-collection").DecisionEntry[]>(`/rd-collection/products/${productId}/decisions`),
  rdCreateDecision: (productId: number, decision: import("./rd-collection").RdDecision, comment = "") =>
    apiFetch<import("./rd-collection").DecisionEntry>(`/rd-collection/products/${productId}/decisions`, {
      method: "POST",
      body: JSON.stringify({ decision, comment }),
    }),
  rdDraftNextOrder: (seasonId: number) =>
    apiFetch<import("./rd-collection").DraftOrderResponse>(`/rd-collection/seasons/${seasonId}/draft-next-order`, {
      method: "POST",
    }),

  // ── «Запуск продукта» ─────────────────────────────────────────────────────

  plchListProducts: () =>
    apiFetch<import("./product-launch").PlchProduct[]>(`/product-launch/products`),
  plchCreateProduct: (body: Partial<import("./product-launch").PlchProduct> & { name: string }) =>
    apiFetch<import("./product-launch").PlchProduct>(`/product-launch/products`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  plchUpdateProduct: (id: number, body: Partial<import("./product-launch").PlchProduct>) =>
    apiFetch<import("./product-launch").PlchProduct>(`/product-launch/products/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  plchDeleteProduct: (id: number) =>
    apiFetch<void>(`/product-launch/products/${id}`, { method: "DELETE" }),

  plchListCompetitors: (productId?: number) =>
    apiFetch<import("./product-launch").PlchCompetitor[]>(
      productId != null
        ? `/product-launch/competitors?product_id=${productId}`
        : `/product-launch/competitors`,
    ),
  plchCreateCompetitor: (body: Partial<import("./product-launch").PlchCompetitor> & { product_id: number }) =>
    apiFetch<import("./product-launch").PlchCompetitor>(`/product-launch/competitors`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  plchUpdateCompetitor: (id: number, body: Partial<import("./product-launch").PlchCompetitor>) =>
    apiFetch<import("./product-launch").PlchCompetitor>(`/product-launch/competitors/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  plchDeleteCompetitor: (id: number) =>
    apiFetch<void>(`/product-launch/competitors/${id}`, { method: "DELETE" }),

  plchAddPhoto: async (productId: number, file: File): Promise<import("./product-launch").PlchProduct> => {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch<import("./product-launch").PlchProduct>(
      `/product-launch/products/${productId}/photos`,
      { method: "POST", body: fd },
    );
  },
  plchDeletePhoto: (photoId: number) =>
    apiFetch<import("./product-launch").PlchProduct>(
      `/product-launch/photos/${photoId}`,
      { method: "DELETE" },
    ),

  plchAddCompetitorPhoto: async (competitorId: number, file: File): Promise<import("./product-launch").PlchCompetitor> => {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch<import("./product-launch").PlchCompetitor>(
      `/product-launch/competitors/${competitorId}/photos`,
      { method: "POST", body: fd },
    );
  },
  plchDeleteCompetitorPhoto: (photoId: number) =>
    apiFetch<import("./product-launch").PlchCompetitor>(
      `/product-launch/competitor-photos/${photoId}`,
      { method: "DELETE" },
    ),

  plchImportSheet: (wipe: boolean) =>
    apiFetch<{
      products_created: number;
      products_updated: number;
      competitors_created: number;
      competitors_updated: number;
      competitors_skipped: number;
    }>(`/product-launch/import-sheet?wipe=${wipe}`, { method: "POST" }),

  // ── Тепловая карта запуска (этапы / ячейки / значения) ────────────────────
  plchListStages: () =>
    apiFetch<import("./product-launch").PlchStage[]>(`/product-launch/heatmap/stages`),
  plchCreateStage: (body: { name: string; sort_order?: number; is_gate?: boolean }) =>
    apiFetch<import("./product-launch").PlchStage>(`/product-launch/heatmap/stages`, { method: "POST", body: JSON.stringify(body) }),
  plchUpdateStage: (id: number, body: Partial<{ name: string; sort_order: number; is_gate: boolean }>) =>
    apiFetch<import("./product-launch").PlchStage>(`/product-launch/heatmap/stages/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  plchDeleteStage: (id: number) =>
    apiFetch<void>(`/product-launch/heatmap/stages/${id}`, { method: "DELETE" }),
  plchSetStageResponsibles: (stageId: number, userIds: string[]) =>
    apiFetch<import("./product-launch").PlchStage>(`/product-launch/heatmap/stages/${stageId}/responsibles`, { method: "PUT", body: JSON.stringify({ user_ids: userIds }) }),
  plchCreateCell: (body: { stage_id: number; name: string; kind?: string; deadline_days?: number; sort_order?: number }) =>
    apiFetch<import("./product-launch").PlchCell>(`/product-launch/heatmap/cells`, { method: "POST", body: JSON.stringify(body) }),
  plchUpdateCell: (id: number, body: Partial<{ name: string; kind: string; deadline_days: number; sort_order: number; stage_id: number }>) =>
    apiFetch<import("./product-launch").PlchCell>(`/product-launch/heatmap/cells/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  plchDeleteCell: (id: number) =>
    apiFetch<void>(`/product-launch/heatmap/cells/${id}`, { method: "DELETE" }),
  plchSetCellResponsibles: (cellId: number, userIds: string[]) =>
    apiFetch<import("./product-launch").PlchCell>(`/product-launch/heatmap/cells/${cellId}/responsibles`, { method: "PUT", body: JSON.stringify({ user_ids: userIds }) }),
  plchTeamMembers: () =>
    apiFetch<import("./product-launch").PlchTeamMember[]>(`/product-launch/heatmap/team-members`),
  plchListCellValues: () =>
    apiFetch<import("./product-launch").PlchCellValue[]>(`/product-launch/heatmap/values`),
  plchSetCellValue: (body: { product_id: number; cell_id: number; done?: boolean; detail?: string; deadline?: string | null }) =>
    apiFetch<import("./product-launch").PlchCellValue>(`/product-launch/heatmap/values`, { method: "PUT", body: JSON.stringify(body) }),
  plchListApprovals: () =>
    apiFetch<import("./product-launch").PlchStageApproval[]>(`/product-launch/heatmap/approvals`),
  plchListStatusHistory: () =>
    apiFetch<import("./product-launch").PlchStatusHistory[]>(`/product-launch/status-history`),
  plchListCategories: () =>
    apiFetch<import("./product-launch").PlchCategory[]>(`/product-launch/categories`),
  plchUpdateCategory: (id: number, body: Partial<{ label: string; description: string; concept: string; accent_color: string; sort_order: number; is_active: boolean }>) =>
    apiFetch<import("./product-launch").PlchCategory>(`/product-launch/categories/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  plchSetApproval: (body: { product_id: number; stage_id: number; approved: boolean }) =>
    apiFetch<import("./product-launch").PlchStageApproval>(`/product-launch/heatmap/approvals`, { method: "PUT", body: JSON.stringify(body) }),
  plchListCellRefs: () =>
    apiFetch<import("./product-launch").PlchCellReference[]>(`/product-launch/heatmap/references`),
  plchAddCellRefLink: (body: { product_id: number; cell_id: number; url: string; label?: string }) =>
    apiFetch<import("./product-launch").PlchCellReference[]>(`/product-launch/heatmap/references/link`, { method: "POST", body: JSON.stringify(body) }),
  plchAddCellRefFile: async (productId: number, cellId: number, file: File, label = ""): Promise<import("./product-launch").PlchCellReference[]> => {
    let buf: ArrayBuffer;
    try { buf = await file.arrayBuffer(); }
    catch { throw new Error(`Не удалось прочитать файл «${file.name}» — возможно, он открыт в другой программе.`); }
    const q = `product_id=${productId}&cell_id=${cellId}&label=${encodeURIComponent(label)}`;
    try {
      // 1) обычный multipart
      const fd = new FormData();
      fd.append("file", new Blob([buf], { type: file.type || "application/octet-stream" }), file.name);
      return await apiFetch<import("./product-launch").PlchCellReference[]>(`/product-launch/heatmap/references/upload?${q}`, { method: "POST", body: fd });
    } catch (e) {
      // 2) фолбэк: base64 в JSON + XOR-обфускация байтов (прячем ZIP/Office-сигнатуру
      //    от контент-сканеров, которые режут такие загрузки — даже base64).
      if (!(e instanceof TypeError)) throw e; // только сетевой сбой
      const bytes = new Uint8Array(buf);
      let bin = "";
      const CH = 0x8000;
      for (let i = 0; i < bytes.length; i += CH) {
        const slice = bytes.subarray(i, i + CH);
        const xored = new Uint8Array(slice.length);
        for (let j = 0; j < slice.length; j++) xored[j] = slice[j] ^ 0xA5;
        bin += String.fromCharCode.apply(null, Array.from(xored));
      }
      const data_b64 = btoa(bin);
      // Имя файла тоже кодируем (UTF-8 → base64), чтобы строка «.xlsx» не светилась в теле запроса.
      const nameBytes = new TextEncoder().encode(file.name);
      let nb = ""; for (let j = 0; j < nameBytes.length; j++) nb += String.fromCharCode(nameBytes[j]);
      const filename_b64 = btoa(nb);
      return apiFetch<import("./product-launch").PlchCellReference[]>(`/product-launch/heatmap/references/blob`, {
        method: "POST",
        body: JSON.stringify({ product_id: productId, cell_id: cellId, label, filename: filename_b64, mime: "", data_b64, enc: "xor" }),
      });
    }
  },
  plchDeleteCellRef: (refId: number) =>
    apiFetch<import("./product-launch").PlchCellReference[]>(`/product-launch/heatmap/references/${refId}`, { method: "DELETE" }),

  // ── Данные вкладки «Задачи» отдела IT (эндпоинты /it-projects) ────────────
  // Модули «IT-проекты» и «Дашборд IT» удалены из портала 2026-07-28 — методы
  // их тепловой карты (продукты, конкуренты, этапы, категории, референсы,
  // импорт) убраны отсюда. Остались календарь, справочник людей и «переезд
  // отделов» — их рисует вкладка «Задачи». 07.08.2026 переехали на /team/*:
  // код виджетов живёт в teamwork, модуль «IT-проекты» больше ни при чём.
  // Имена методов оставлены прежними (itpr*), чтобы не трогать виджеты.

  itprCalendarEvents: (dateFrom?: string, dateTo?: string) =>
    apiFetch<{ configured: boolean; events: { date: string; time: string | null; end?: string | null; title: string; description?: string; location?: string; meet?: string }[]; error?: string }>(
      `/team/calendar/events${dateFrom || dateTo ? `?date_from=${dateFrom ?? ""}&date_to=${dateTo ?? ""}` : ""}`),
  itprGetCalendarSettings: () =>
    apiFetch<{ ical_url: string }>(`/team/calendar/settings`),
  itprSetCalendarSettings: (icalUrl: string) =>
    apiFetch<{ ical_url: string }>(`/team/calendar/settings`, { method: "PUT", body: JSON.stringify({ ical_url: icalUrl }) }),
  // Нативные события календаря (портальные, приватные: участник/автор).
  itprListPeople: () =>
    apiFetch<import("./it-tasks").CalendarPerson[]>(`/team/people`),
  itprListNativeEvents: (dateFrom?: string, dateTo?: string) =>
    apiFetch<{ events: import("./it-tasks").NativeCalendarEvent[]; can_create: boolean }>(
      `/team/calendar/native${dateFrom || dateTo ? `?date_from=${dateFrom ?? ""}&date_to=${dateTo ?? ""}` : ""}`),
  itprCreateEvent: (body: import("./it-tasks").NativeEventInput) =>
    apiFetch<import("./it-tasks").NativeCalendarEvent>(`/team/calendar/native`, { method: "POST", body: JSON.stringify(body) }),
  itprPatchEvent: (id: number, body: Partial<import("./it-tasks").NativeEventInput>) =>
    apiFetch<import("./it-tasks").NativeCalendarEvent>(`/team/calendar/native/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  itprDeleteEvent: (id: number) =>
    apiFetch<void>(`/team/calendar/native/${id}`, { method: "DELETE" }),
  itprListDeptMigration: () =>
    apiFetch<import("./it-tasks").ItprDeptMigration[]>(`/team/dept-migration`),
  itprListDeptTables: () =>
    apiFetch<import("./it-tasks").ItprDeptTable[]>(`/team/dept-tables`),
  itprCreateDeptTable: (body: Partial<import("./it-tasks").ItprDeptTable> & { dept_key: string }) =>
    apiFetch<import("./it-tasks").ItprDeptTable>(`/team/dept-tables`, { method: "POST", body: JSON.stringify(body) }),
  itprPatchDeptTable: (id: number, body: Partial<import("./it-tasks").ItprDeptTable>) =>
    apiFetch<import("./it-tasks").ItprDeptTable>(`/team/dept-tables/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  itprDeleteDeptTable: (id: number) =>
    apiFetch<void>(`/team/dept-tables/${id}`, { method: "DELETE" }),

  // ── «Трекер разработок» ───────────────────────────────────────────────────

  dvtrListProducts: () =>
    apiFetch<import("./dev-tracker").DvtrProduct[]>(`/dev-tracker/products`),
  dvtrCreateProduct: (body: Partial<import("./dev-tracker").DvtrProduct> & { name: string }) =>
    apiFetch<import("./dev-tracker").DvtrProduct>(`/dev-tracker/products`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  dvtrUpdateProduct: (id: number, body: Partial<import("./dev-tracker").DvtrProduct>) =>
    apiFetch<import("./dev-tracker").DvtrProduct>(`/dev-tracker/products/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  dvtrDeleteProduct: (id: number) =>
    apiFetch<void>(`/dev-tracker/products/${id}`, { method: "DELETE" }),

  dvtrAddPhoto: async (productId: number, file: File): Promise<import("./dev-tracker").DvtrProduct> => {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch<import("./dev-tracker").DvtrProduct>(
      `/dev-tracker/products/${productId}/photos`,
      { method: "POST", body: fd },
    );
  },
  dvtrDeletePhoto: (photoId: number) =>
    apiFetch<import("./dev-tracker").DvtrProduct>(
      `/dev-tracker/photos/${photoId}`,
      { method: "DELETE" },
    ),

  dvtrAddReferenceLink: (productId: number, body: { url: string; label?: string }) =>
    apiFetch<import("./dev-tracker").DvtrProduct>(
      `/dev-tracker/products/${productId}/references/link`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  dvtrAddReferenceImage: async (productId: number, file: File, label?: string): Promise<import("./dev-tracker").DvtrProduct> => {
    const fd = new FormData();
    fd.append("file", file);
    if (label) fd.append("label", label);
    return apiFetch<import("./dev-tracker").DvtrProduct>(
      `/dev-tracker/products/${productId}/references/image`,
      { method: "POST", body: fd },
    );
  },
  dvtrUpdateReference: (refId: number, body: { label?: string; url?: string }) =>
    apiFetch<import("./dev-tracker").DvtrProduct>(
      `/dev-tracker/references/${refId}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),
  dvtrDeleteReference: (refId: number) =>
    apiFetch<import("./dev-tracker").DvtrProduct>(
      `/dev-tracker/references/${refId}`,
      { method: "DELETE" },
    ),

  dvtrImportSheet: (sheetId: string, wipe: boolean) =>
    apiFetch<{
      products_created: number;
      products_updated: number;
      competitors_created: number;
      competitors_updated: number;
      competitors_skipped: number;
    }>(`/dev-tracker/import-sheet?sheet_id=${encodeURIComponent(sheetId)}&wipe=${wipe}`, { method: "POST" }),

  // ── «Закуп ДС» ────────────────────────────────────────────────────────────

  plYucTrends: (cabinetId: string) =>
    apiFetch<import("./planning").PlYucTrendMeta[]>(
      `/planning/yuc/trends?cabinet_id=${encodeURIComponent(cabinetId)}`,
    ),

  plYucImportTrend: (cabinetId: string, label: string, file: File, category = "") => {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch<{ ok: boolean; scope_key: string; rows: number; trend: import("./planning").PlYucTrendMeta }>(
      `/planning/yuc/import-trend?cabinet_id=${encodeURIComponent(cabinetId)}&label=${encodeURIComponent(label)}&category=${encodeURIComponent(category)}`,
      { method: "POST", body: fd },
    );
  },

  /** Загрузка месячного плана (штуки по SKU) из xlsx. dryRun — только предпросмотр. */
  plYucImportPlan: (file: File, month: string, opts: { mode?: string; dryRun?: boolean } = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    const q = new URLSearchParams({
      month,
      mode: opts.mode ?? "selloff",
      dry_run: String(opts.dryRun ?? true),
    });
    return apiFetch<import("./planning").PlYucImportResult>(
      `/planning/yuc/import-plan?${q.toString()}`,
      { method: "POST", body: fd },
    );
  },

  plYucTrend: (cabinetId: string, scopeKey: string, method = "none", signal = "orders_pcs") =>
    apiFetch<import("./planning").PlYucTrendDetail>(
      `/planning/yuc/trend?cabinet_id=${encodeURIComponent(cabinetId)}&scope_key=${encodeURIComponent(scopeKey)}&method=${method}&signal=${signal}`,
    ),

  plYucTrendAvg: (cabinetId: string, scopeKeys: string[], method = "none", signal = "orders_pcs") =>
    apiFetch<import("./planning").PlYucTrendAvg>(
      `/planning/yuc/trend-avg?cabinet_id=${encodeURIComponent(cabinetId)}&scope_keys=${encodeURIComponent(scopeKeys.join(","))}&method=${method}&signal=${signal}`,
    ),

  plYucDeleteTrend: (cabinetId: string, scopeKey: string) =>
    apiFetch<{ ok: boolean }>(
      `/planning/yuc/trend?cabinet_id=${encodeURIComponent(cabinetId)}&scope_key=${encodeURIComponent(scopeKey)}`,
      { method: "DELETE" },
    ),

  // Задать/снять категорию у трендов (bulk по scope_key).
  plYucSetTrendCategory: (cabinetId: string, scopeKeys: string[], category: string | null) =>
    apiFetch<{ ok: boolean; updated: number }>(
      `/planning/yuc/trend-category?cabinet_id=${encodeURIComponent(cabinetId)}`,
      { method: "POST", body: JSON.stringify({ scope_keys: scopeKeys, category }) },
    ),

  // Удалить категорию как метку: снять с трендов + отвязать @cat-SKU (данные трендов сохраняются).
  plYucDeleteCategory: (cabinetId: string, name: string) =>
    apiFetch<{ ok: boolean; cleared: number; unbound: number }>(
      `/planning/yuc/category?cabinet_id=${encodeURIComponent(cabinetId)}&name=${encodeURIComponent(name)}`,
      { method: "DELETE" },
    ),

  plYucSkus: (cabinetId: string) =>
    apiFetch<import("./planning").PlYucSku[]>(
      `/planning/yuc/skus?cabinet_id=${encodeURIComponent(cabinetId)}`,
    ),

  plYucSkuVisibility: (cabinetId: string, sku: string, hidden: boolean) =>
    apiFetch<{ ok: boolean }>(
      `/planning/yuc/sku-visibility?cabinet_id=${encodeURIComponent(cabinetId)}`,
      { method: "PUT", body: JSON.stringify({ sku, hidden }) },
    ),

  plYucReport: (cabinetId: string, sku: string, real = false) =>
    apiFetch<import("./planning").PlYucReport>(
      `/planning/yuc/report?cabinet_id=${encodeURIComponent(cabinetId)}&sku=${encodeURIComponent(sku)}${real ? "&real=1" : ""}`,
    ),

  plYucCabinetSettings: (cabinetId: string) =>
    apiFetch<{ cabinetId: string; targetBuyoutPct: number | null }>(
      `/planning/yuc/cabinet-settings?cabinet_id=${encodeURIComponent(cabinetId)}`,
    ),

  plYucSaveCabinetSettings: (cabinetId: string, targetBuyoutPct: number | null) =>
    apiFetch<{ ok: boolean; targetBuyoutPct: number | null }>(
      "/planning/yuc/cabinet-settings",
      { method: "PUT", body: JSON.stringify({ cabinet_id: cabinetId, target_buyout_pct: targetBuyoutPct }) },
    ),

  plYucSummary: (cabinetId: string, real = false) =>
    apiFetch<import("./planning").PlYucSummaryRow[]>(
      `/planning/yuc/summary?cabinet_id=${encodeURIComponent(cabinetId)}${real ? "&real=1" : ""}`,
    ),

  plYucMonthlySummary: (cabinetId: string, real = false) =>
    apiFetch<import("./planning").PlYucMonthlySummaryRow[]>(
      `/planning/yuc/monthly-summary?cabinet_id=${encodeURIComponent(cabinetId)}${real ? "&real=1" : ""}`,
    ),

  plYucDaily: (cabinetId: string, sku: string, real = false) =>
    apiFetch<import("./planning").PlYucDaily>(
      `/planning/yuc/daily?cabinet_id=${encodeURIComponent(cabinetId)}&sku=${encodeURIComponent(sku)}${real ? "&real=1" : ""}`,
    ),

  plYucMonthPlan: (cabinetId: string, ym: string, real = false) =>
    apiFetch<import("./planning").PlYucMonthPlan>(
      `/planning/yuc/month-plan?cabinet_id=${encodeURIComponent(cabinetId)}&ym=${encodeURIComponent(ym)}${real ? "&real=1" : ""}`,
    ),

  plYucHeader: (
    cabinetId: string,
    body: { sku: string } & Record<string, string | number | null>,
  ) =>
    apiFetch<{ ok: boolean }>(
      `/planning/yuc/header?cabinet_id=${encodeURIComponent(cabinetId)}`,
      { method: "PUT", body: JSON.stringify(body) },
    ),

  /** План продаж месяца с перераспределением остатка по сезонному тренду.
   *  mode=keep_season — общий объём сезона сохраняется (разница расходится по
   *  будущим месяцам), change_season — правим только этот месяц. */
  plYucMonthBuyouts: (
    cabinetId: string,
    body: { sku: string; month_index: number; buyouts: number | null; mode: "keep_season" | "change_season" },
  ) =>
    apiFetch<{ ok: boolean; redistributed: number }>(
      `/planning/yuc/month-buyouts?cabinet_id=${encodeURIComponent(cabinetId)}`,
      { method: "PUT", body: JSON.stringify(body) },
    ),

  plYucMonthCell: (
    cabinetId: string,
    body: { sku: string; month_index: number; field: import("./planning").PlYucMonthField; value: number | string | null },
  ) =>
    apiFetch<{ ok: boolean }>(
      `/planning/yuc/month-cell?cabinet_id=${encodeURIComponent(cabinetId)}`,
      { method: "PUT", body: JSON.stringify(body) },
    ),

  getSalesReportPlan: () =>
    apiFetch<SalesReportPlanResponse>("/modules/sales/sales-report-plan"),

  /** Перезаписывает план месяца целиком: кабинет → показатели (null = убрать). */
  saveSalesReportPlan: (month: string, plan: Record<string, SalesReportPlanRow>) =>
    apiFetch<{ ok: boolean; month: string; cabinets: number }>(
      `/modules/sales/sales-report-plan/${month}`,
      { method: "PUT", body: JSON.stringify(plan) },
    ),

  // ── Dashboards (aggregated summaries per department) ─────────────────────
  /** period: "month" — календарный месяц (как ГПР), "days" — скользящее окно.
   *  month: якорный месяц YYYY-MM (по умолчанию текущий) — чтобы смотреть закрытые периоды. */
  getSalesDashboardSummary: (
    days = 30,
    real = false,
    period: "month" | "days" = "month",
    month?: string,
  ) =>
    apiFetch<SalesDashboardSummary>(
      `/dashboards/sales/summary?days=${days}&period=${period}${month ? `&month=${month}` : ""}${real ? "&real=1" : ""}`,
    ),

  // ── HR (справочник, оргструктура/ФОТ, отпуска) ───────────────────────────
  hrListEmployees: () =>
    apiFetch<import("./hr").HrEmployee[]>(`/hr/employees`),
  /** Своя карточка — работает без права на модуль HR (для своего профиля). */
  hrMyEmployee: () =>
    apiFetch<import("./hr").HrEmployee | null>(`/hr/employees/me`),
  hrCreateEmployee: (body: import("./hr").HrEmployeeInput) =>
    apiFetch<import("./hr").HrEmployee>(`/hr/employees`, { method: "POST", body: JSON.stringify(body) }),
  hrUpdateEmployee: (id: number, body: Partial<import("./hr").HrEmployeeInput>) =>
    apiFetch<import("./hr").HrEmployee>(`/hr/employees/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  /** Фото в карточку сотрудника. Файл ложится туда же, где аватары портала. */
  hrUploadEmployeePhoto: async (id: number, file: File) => {
    const form = new FormData();
    form.append("file", file);
    const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_STORAGE_KEY) : null;
    const res = await fetch(`${API_BASE}/hr/employees/${id}/photo`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!res.ok) throw new Error(await res.text());
    invalidateApiCache();
    return (await res.json()) as import("./hr").HrEmployee;
  },
  hrDeleteEmployeePhoto: (id: number) =>
    apiFetch<import("./hr").HrEmployee>(`/hr/employees/${id}/photo`, { method: "DELETE" }),
  hrDeleteEmployee: (id: number) =>
    apiFetch<{ status: string }>(`/hr/employees/${id}`, { method: "DELETE" }),
  // ── Профиль: цели (OKR), оценки, выплаты ────────────────────────────────
  hrListGoals: (empId: number) =>
    apiFetch<import("./hr").HrGoal[]>(`/hr/employees/${empId}/goals`),
  hrCreateGoal: (body: import("./hr").HrGoalInput) =>
    apiFetch<import("./hr").HrGoal>(`/hr/goals`, { method: "POST", body: JSON.stringify(body) }),
  hrPatchGoal: (id: number, body: Partial<import("./hr").HrGoalInput>) =>
    apiFetch<import("./hr").HrGoal>(`/hr/goals/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  hrDeleteGoal: (id: number) =>
    apiFetch<{ status: string }>(`/hr/goals/${id}`, { method: "DELETE" }),

  hrListReviews: (empId: number) =>
    apiFetch<import("./hr").HrReview[]>(`/hr/employees/${empId}/reviews`),
  hrCreateReview: (body: import("./hr").HrReviewInput) =>
    apiFetch<import("./hr").HrReview>(`/hr/reviews`, { method: "POST", body: JSON.stringify(body) }),
  hrPatchReview: (id: number, body: Partial<import("./hr").HrReviewInput>) =>
    apiFetch<import("./hr").HrReview>(`/hr/reviews/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  hrDeleteReview: (id: number) =>
    apiFetch<{ status: string }>(`/hr/reviews/${id}`, { method: "DELETE" }),

  hrListPayouts: (empId: number) =>
    apiFetch<import("./hr").HrPayout[]>(`/hr/employees/${empId}/payouts`),
  hrCreatePayout: (body: import("./hr").HrPayoutInput) =>
    apiFetch<import("./hr").HrPayout>(`/hr/payouts`, { method: "POST", body: JSON.stringify(body) }),
  hrPatchPayout: (id: number, body: Partial<import("./hr").HrPayoutInput>) =>
    apiFetch<import("./hr").HrPayout>(`/hr/payouts/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  hrDeletePayout: (id: number) =>
    apiFetch<{ status: string }>(`/hr/payouts/${id}`, { method: "DELETE" }),

  hrListBirthdays: (days?: number) =>
    apiFetch<import("./hr").HrBirthday[]>(`/hr/birthdays${days != null ? `?days=${days}` : ""}`),
  /** Блок дашборда «Люди в движении»: days — окно назад (0 = за всё время), horizon — вперёд. */
  hrMovement: (days?: number, horizon?: number) => {
    const qs = new URLSearchParams();
    if (days != null) qs.set("days", String(days));
    if (horizon != null) qs.set("horizon", String(horizon));
    const s = qs.toString();
    return apiFetch<import("./hr").HrMovement>(`/hr/movement${s ? `?${s}` : ""}`);
  },
  hrListVacations: (year?: number) =>
    apiFetch<import("./hr").HrVacation[]>(`/hr/vacations${year != null ? `?year=${year}` : ""}`),
  hrCreateVacation: (body: import("./hr").HrVacationInput) =>
    apiFetch<import("./hr").HrVacation>(`/hr/vacations`, { method: "POST", body: JSON.stringify(body) }),
  hrUpdateVacation: (id: number, body: Partial<import("./hr").HrVacationInput>) =>
    apiFetch<import("./hr").HrVacation>(`/hr/vacations/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  hrDeleteVacation: (id: number) =>
    apiFetch<{ status: string }>(`/hr/vacations/${id}`, { method: "DELETE" }),
  // Найм
  hrListVacancies: () =>
    apiFetch<import("./hr").HrVacancy[]>(`/hr/vacancies`),
  hrCreateVacancy: (body: import("./hr").HrVacancyInput) =>
    apiFetch<import("./hr").HrVacancy>(`/hr/vacancies`, { method: "POST", body: JSON.stringify(body) }),
  hrUpdateVacancy: (id: number, body: Partial<import("./hr").HrVacancyInput>) =>
    apiFetch<import("./hr").HrVacancy>(`/hr/vacancies/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  hrDeleteVacancy: (id: number) =>
    apiFetch<{ status: string }>(`/hr/vacancies/${id}`, { method: "DELETE" }),
  hrListCandidates: (vacancy?: string) =>
    apiFetch<import("./hr").HrCandidate[]>(`/hr/candidates${vacancy ? `?vacancy=${encodeURIComponent(vacancy)}` : ""}`),
  hrCreateCandidate: (body: import("./hr").HrCandidateInput) =>
    apiFetch<import("./hr").HrCandidate>(`/hr/candidates`, { method: "POST", body: JSON.stringify(body) }),
  hrUpdateCandidate: (id: number, body: Partial<import("./hr").HrCandidateInput>) =>
    apiFetch<import("./hr").HrCandidate>(`/hr/candidates/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  hrDeleteCandidate: (id: number) =>
    apiFetch<{ status: string }>(`/hr/candidates/${id}`, { method: "DELETE" }),
  hrScoreCandidate: (id: number, jobDescription?: string) =>
    apiFetch<import("./hr").HrCandidate>(`/hr/candidates/${id}/score`, {
      method: "POST",
      body: JSON.stringify({ job_description: jobDescription ?? "" }),
    }),
  // Аналитика воронки найма (агрегаты; days=0/undefined — за всё время)
  hrHiringAnalytics: (days?: number, vacancy?: string) => {
    const p = new URLSearchParams();
    if (days) p.set("days", String(days));
    if (vacancy) p.set("vacancy", vacancy);
    const qs = p.toString();
    return apiFetch<import("./hr").HrHiringAnalytics>(`/hr/hiring/analytics${qs ? `?${qs}` : ""}`);
  },
  /** Полное резюме кандидата с hh.ru (ленивая загрузка + кэш на бэке). */
  hrCandidateResume: (id: number, refresh = false) =>
    apiFetch<import("./hr").HrResumeResponse>(`/hr/candidates/${id}/resume${refresh ? "?refresh=true" : ""}`),
  hrListCandidateNotes: (cid: number) =>
    apiFetch<import("./hr").HrCandidateNote[]>(`/hr/candidates/${cid}/notes`),
  hrAddCandidateNote: (cid: number, text: string) =>
    apiFetch<import("./hr").HrCandidateNote>(`/hr/candidates/${cid}/notes`, { method: "POST", body: JSON.stringify({ text }) }),
  hrDeleteCandidateNote: (cid: number, nid: number) =>
    apiFetch<{ status: string }>(`/hr/candidates/${cid}/notes/${nid}`, { method: "DELETE" }),
  // Договоры
  hrListContractTemplates: () =>
    apiFetch<import("./hr").HrContractTemplate[]>(`/hr/contract-templates`),
  hrUploadContractTemplate: (name: string, file: File) => {
    const fd = new FormData();
    fd.append("name", name);
    fd.append("file", file);
    return apiFetch<import("./hr").HrContractTemplate>(`/hr/contract-templates`, { method: "POST", body: fd });
  },
  hrDeleteContractTemplate: (id: number) =>
    apiFetch<{ status: string }>(`/hr/contract-templates/${id}`, { method: "DELETE" }),
  hrListContracts: (employeeId: number) =>
    apiFetch<import("./hr").HrContract[]>(`/hr/contracts?employee_id=${employeeId}`),
  hrGenerateContract: (employeeId: number, templateId: number) =>
    apiFetch<import("./hr").HrContract>(`/hr/employees/${employeeId}/contract?template_id=${templateId}`, { method: "POST" }),
  hrDownloadContract: (cid: number) =>
    apiBlobFetch(`/hr/contracts/${cid}/download`),
  hrOnboardingContract: (oid: number, templateId: number, employeeId?: number, markSent = true) => {
    const qs = new URLSearchParams({ template_id: String(templateId), mark_sent: String(markSent) });
    if (employeeId != null) qs.set("employee_id", String(employeeId));
    return apiFetch<import("./hr").HrContract>(`/hr/onboarding/${oid}/contract?${qs.toString()}`, { method: "POST" });
  },
  hrDeleteContract: (cid: number) =>
    apiFetch<{ status: string }>(`/hr/contracts/${cid}`, { method: "DELETE" }),
  // Онбординг
  // employeeId — строки одного человека (карточка сотрудника), без него — все.
  hrListOnboarding: (employeeId?: number) =>
    apiFetch<import("./hr").HrOnboarding[]>(
      `/hr/onboarding${employeeId != null ? `?employee_id=${employeeId}` : ""}`,
    ),
  hrCreateOnboarding: (body: import("./hr").HrOnboardingInput) =>
    apiFetch<import("./hr").HrOnboarding>(`/hr/onboarding`, { method: "POST", body: JSON.stringify(body) }),
  hrUpdateOnboarding: (id: number, body: Partial<import("./hr").HrOnboardingInput>) =>
    apiFetch<import("./hr").HrOnboarding>(`/hr/onboarding/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  hrDeleteOnboarding: (id: number) =>
    apiFetch<{ status: string }>(`/hr/onboarding/${id}`, { method: "DELETE" }),
  hrListOffboarding: (employeeId?: number) =>
    apiFetch<import("./hr").HrOffboarding[]>(
      `/hr/offboarding${employeeId != null ? `?employee_id=${employeeId}` : ""}`,
    ),
  hrCreateOffboarding: (body: import("./hr").HrOffboardingInput) =>
    apiFetch<import("./hr").HrOffboarding>(`/hr/offboarding`, { method: "POST", body: JSON.stringify(body) }),
  hrUpdateOffboarding: (id: number, body: Partial<import("./hr").HrOffboardingInput>) =>
    apiFetch<import("./hr").HrOffboarding>(`/hr/offboarding/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  hrDeleteOffboarding: (id: number) =>
    apiFetch<{ status: string }>(`/hr/offboarding/${id}`, { method: "DELETE" }),
  // Документы сотрудника (сканы личного дела и рабочие бумаги)
  hrDocTypes: () =>
    apiFetch<import("./hr").HrDocType[]>(`/hr/doc-types`),
  hrListDocuments: (employeeId: number) =>
    apiFetch<import("./hr").HrEmployeeDocument[]>(`/hr/employees/${employeeId}/documents`),
  hrUploadDocument: (employeeId: number, docType: string, file: File, title = "", note = "") => {
    const fd = new FormData();
    fd.append("doc_type", docType);
    fd.append("title", title);
    fd.append("note", note);
    fd.append("file", file);
    return apiFetch<import("./hr").HrEmployeeDocument>(`/hr/employees/${employeeId}/documents`, { method: "POST", body: fd });
  },
  hrDownloadDocument: (id: number) =>
    apiBlobFetch(`/hr/documents/${id}/download`),
  hrDeleteDocument: (id: number) =>
    apiFetch<{ status: string }>(`/hr/documents/${id}`, { method: "DELETE" }),
  /** Свод «Стоимость найма» по позициям: размещения, стоимость, отклики, просмотры. */
  hrHiringCost: () => apiFetch<import("./hr").HrHiringCostRow[]>(`/hr/hiring-cost`),
  /** Прайс размещений hh: тип публикации → цена. */
  hrHhBillingPrices: () => apiFetch<import("./hr").HrHhBillingPrice[]>(`/hr/hh/billing-prices`),
  /** Подтянуть цены из hh (доступны только для апгрейдов активных вакансий). */
  hrHhPullBillingPrices: () =>
    apiFetch<import("./hr").HrHhBillingPrice[]>(`/hr/hh/billing-prices/from-hh`, { method: "POST" }),
  hrHhSaveBillingPrices: (body: { type_id: string; label?: string; price: number }[]) =>
    apiFetch<import("./hr").HrHhBillingPrice[]>(`/hr/hh/billing-prices`, {
      method: "PUT", body: JSON.stringify(body),
    }),
  // hh.ru интеграция
  hrHhStatus: () =>
    apiFetch<import("./hr").HrHhStatus>(`/hr/hh/status`),
  hrHhSaveConfig: (body: { client_id: string; client_secret: string; redirect_uri: string; user_agent?: string }) =>
    apiFetch<import("./hr").HrHhStatus>(`/hr/hh/config`, { method: "PUT", body: JSON.stringify(body) }),
  hrHhAuthorizeUrl: () =>
    apiFetch<{ url: string }>(`/hr/hh/authorize-url`),
  hrHhSync: () =>
    apiFetch<{ status: string; vacancies: number; vacancies_archived: number; candidates_seen: number; candidates_new: number }>(`/hr/hh/sync`, { method: "POST" }),
  hrHhDisconnect: () =>
    apiFetch<import("./hr").HrHhStatus>(`/hr/hh/disconnect`, { method: "POST" }),

  // ── Калькулятор прибыли вне карточки (страница /calculator) ────────────────
  // Свои эндпоинты, а не /assortment/*: страница открыта любому сотруднику,
  // права на модуль «Ассортимент» для неё не нужны.
  /** Тарифы складов WB (логистика/хранение/приёмка) без права на «Ассортимент». */
  profitCalcWarehouses: () =>
    apiFetch<{ warehouses: import("./assortment").MpWarehouse[]; syncedAt: string | null; error: string | null }>(
      `/profit-calc/warehouses`),
  /** Сохранённые расчёты текущего пользователя (свои — чужих не видно). */
  profitCalcSaved: () => apiFetch<SavedProfitCalc[]>(`/profit-calc/saved`),
  profitCalcCreate: (body: SavedProfitCalcIn) =>
    apiFetch<SavedProfitCalc>(`/profit-calc/saved`, { method: "POST", body: JSON.stringify(body) }),
  profitCalcUpdate: (id: number, body: SavedProfitCalcIn) =>
    apiFetch<SavedProfitCalc>(`/profit-calc/saved/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  profitCalcDelete: (id: number) =>
    apiFetch<void>(`/profit-calc/saved/${id}`, { method: "DELETE" }),
};

/** Именованный расчёт калькулятора прибыли. `inputs` — полное состояние ввода
 *  (ProfitInputs), `result` — итоги на момент сохранения (для списка). */
export type SavedProfitCalc = {
  id: number;
  name: string;
  inputs: Record<string, number | string | boolean>;
  result: { marginPct?: number; roiPct?: number; netProfit?: number; priceWb?: number };
  createdAt: string;
  updatedAt: string;
};
export type SavedProfitCalcIn = Omit<SavedProfitCalc, "id" | "createdAt" | "updatedAt">;

/** Показатели одного разреза за окно — тот же состав, что на вкладке «Отчёт». */
// ── План ГПР (редактор в «Настройках → Продажи · WB») ───────────────────────
// null у поля = «плана нет»: карточка на дашборде напишет «ориентира нет»,
// а 0 — это честный ноль. Поэтому именно null, а не 0 по умолчанию.
export type SalesReportPlanRow = {
  /** Плановая маржа ₽ — считается скриптом, руками не правится (поле только для чтения). */
  marginRub?: number | null;
  /** Плановый ДРР, % ОТ ВЫКУПОВ. Один на все артикулы, задаётся менеджером. */
  drrPct?: number | null;
  ordersSum: number | null;
  ordersCount: number | null;
  buyoutsSum: number | null;
  buyoutsCount: number | null;
  marginPct: number | null;
};

export type SalesReportPlanResponse = {
  months: Record<string, Record<string, SalesReportPlanRow & { source?: string }>>;
  cabinets: { id: string; title: string }[];
  note?: string | null;
};

export type SalesScopeMetrics = {
  orders_rub: number;
  orders_cnt: number;
  buyouts_rub: number;
  buyouts_cnt: number;
  returns_cnt: number;
  /** Выкуплено шт ÷ заказано шт (как в «Отчёте»). */
  buyout_pct: number | null;
  /** Когорта воронки заказ→выкуп — справочно, расходится с отчётной цифрой. */
  buyout_pct_cohort: number | null;
  adv_rub: number;
  /** Откручено бонусами WB — СПРАВОЧНО, в расход и маржу не входит (решение
   *  Алины 19.08.2026). null = бонусов не было либо период старше 21.04.2026,
   *  за который выгрузки всех списаний ещё нет. */
  adv_bonus_rub?: number | null;
  drr_orders: number | null;
  drr_buyouts: number | null;
  logistic_rub: number;
  logistic_pct: number | null;
  margin_rub: number;
  margin_pct: number | null;
  /** База процента маржи — розничная выручка финотчёта. Нужна, чтобы перевести
   *  ПЛАНОВЫЙ процент маржи из ГПР в рубли по той же базе, что и факт. */
  margin_base: number;
  /** Маржа строго по дням реального финотчёта (без достройки оценкой). */
  margin_real_rub: number;
  margin_real_pct: number | null;
  /** Вернули ÷ (выкупили + вернули). */
  returns_pct: number | null;
  avg_check: number;
  ppvz_rub: number;
  cost_rub: number;
  tax_rub: number;
  acquiring_rub: number;
  extra_rub: number;
  real_days: number;
  est_days: number;
};

/** Ориентиры для метрик, у которых в ГПР нет плана: предыдущий сопоставимый период. */
export type SalesScopeBench = {
  buyout_pct: number | null;
  buyout_pct_source: "plan" | "prev";
  drr_orders: number | null;
  drr_buyouts: number | null;
  logistic_pct: number | null;
  logistic_days: number;
  margin_pct: number | null;
  returns_pct: number | null;
  orders_rub: number;
  buyouts_rub: number;
  margin_rub: number;
};

/** План ГПР на месяц (sales_report_plan.json) — тот же источник, что у «Отчёта». */
/** Остатки разреза: у «Все кабинеты» — общий итог, у кабинета — его собственный. */
export type SalesScopeStocks = SalesDashboardSummary["stocks"];

export type SalesScopePlan = {
  orders_rub: number | null;
  buyouts_rub: number | null;
  orders_cnt: number | null;
  buyouts_cnt: number | null;
  margin_pct: number | null;
  /** Плановая маржа ₽ за месяц: юнитка × плановые выкупы − реклама по drr_pct.
   *  Считается скриптом recalc_report_plan_rub.py, не выводится из margin_pct. */
  margin_rub: number | null;
  /** Плановый ДРР, % ОТ ВЫКУПОВ — один на все артикулы, задаётся вручную. */
  drr_pct: number | null;
};

export type SalesScopeProjection = {
  orders_rub: number;
  buyouts_rub: number;
  margin_rub: number;
  orders_cnt: number;
  buyouts_cnt: number;
};

export type SalesScopeDay = {
  day: string;
  orders_rub: number;
  orders_cnt: number;
  buyouts_rub: number;
  buyouts_cnt: number;
  margin_rub: number;
  adv_rub: number;
  logistic_rub: number;
  estimated: boolean;
};

/** Разрез дашборда: «Все кабинеты» (id="all") или конкретный кабинет. */
export type SalesScope = {
  id: string;
  title: string;
  brand: string;
  /** По какой день посчитано реальным финотчётом (дальше — оценка). */
  coverage_to: string | null;
  /** Остатки разреза: у «Все кабинеты» — общий итог, у кабинета — его собственный.
   *  Может отсутствовать у старых ответов сервера — тогда берём общий блок. */
  stocks?: SalesScopeStocks | null;
  metrics: SalesScopeMetrics;
  /** Календарный месяц — окно, с которым сопоставляется план (он всегда месячный). */
  month: SalesScopeMetrics;
  bench: SalesScopeBench;
  plan: SalesScopePlan | null;
  /** «Идём на» для выбранного окна (null, когда окно уже закрыто). */
  projection: SalesScopeProjection | null;
  month_projection: SalesScopeProjection | null;
  daily: SalesScopeDay[];
};

export type SalesDashboardSummary = {
  period_days: number;
  date_from: string;
  date_to: string;
  period: {
    mode: "month" | "days";
    label: string;
    from: string;
    to: string;
    elapsed_days: number;
    total_days: number;
    prev_from: string;
    prev_to: string;
    month_from: string;
    month_label: string;
    month_elapsed_days: number;
    month_days: number;
    /** Месяц действительно завершился (последний день позади). */
    is_closed: boolean;
    /** Последний день, который реально был в источниках (null — данных нет вовсе). */
    data_through?: string | null;
    /** Сколько календарных дней после него пусты. 0 — синхронизация не отстаёт. */
    stale_days?: number;
  };
  scopes: SalesScope[];
  // legacy (обратная совместимость)
  total_revenue: number;
  total_orders: number;
  today_revenue: number;
  today_orders: number;
  revenue_delta_pct: number;
  totals: {
    buyouts_rub: number;
    buyouts_cnt: number;
    orders_rub: number;
    orders_cnt: number;
    returns_cnt: number;
    returns_rub: number;
    margin_rub: number;
    adv_rub: number;
  };
  today: {
    buyouts_rub: number;
    buyouts_cnt: number;
    orders_rub: number;
    orders_cnt: number;
    margin_rub: number;
    adv_rub: number;
  };
  plan: {
    plan_rub: number | null;
    fact_rub: number | null;
    progress_pct: number | null;
    month_label: string | null;
  };
  ads: {
    drr_orders_today_pct: number;
    drr_buyouts_today_pct: number;
    drr_orders_month_pct: number;
    drr_buyouts_month_pct: number;
    avg_buyout_pct: number;
    localization_index: number | null;
    localization_period_from: string | null;
    localization_period_to: string | null;
  };
  stocks: {
    warehouse_qty: number;
    in_way_to_client_qty: number;
    in_way_from_client_qty: number;
    warehouse_rub: number | null;
    in_way_to_client_rub: number | null;
    in_way_from_client_rub: number | null;
    cost_coverage_pct: number;
  };
  daily: Array<{
    day: string;
    revenue: number;
    orders: number;
    avg_check: number;
    orders_rub: number;
    orders_cnt: number;
    buyouts_rub: number;
    buyouts_cnt: number;
    margin_rub: number;
    adv_rub: number;
    logistic_rub: number;
    /** Выкупы/маржа за день — предварительная оценка (финотчёт ещё не пришёл). */
    estimated: boolean;
  }>;
  by_cabinet: Array<{
    id: string;
    title: string;
    revenue: number;
    orders: number;
    orders_rub: number;
    orders_cnt: number;
    buyouts_rub: number;
    buyouts_cnt: number;
  }>;
};
